/**
 * DCG Decklist Builder — Desert City Games TCG
 * Layout: Decklist | Inventory | Recommendations, with a full-width Cart band.
 *
 * ── v1.4 — dead-code removal, 03 AUG 2026 ────────────────────────────────
 * Audited by enumerating all 113 definitions and counting references, then
 * checking two reachability paths a plain grep misses: `data-action` string
 * dispatch, and whether any template literal emits the markup a handler needs.
 *
 * REMOVED — 3 symbols, 60 lines. Each defined exactly once, referenced
 * nowhere, with no string-dispatch entry and no markup able to reach it:
 *   • Api.searchByTitle                — BinderPOS title search, never called
 *   • Api.autocompleteProducts         — BinderPOS autocomplete, never called
 *   • _showStockAdjustmentNotification — built a .dcg-stock-notice element
 *     that only it produced, and which has no CSS anywhere in the theme
 *
 * DELIBERATELY KEPT despite looking dead — both were wrongly listed as dead
 * in the 03 AUG storefront diagnostic; live inspection corrected that:
 *
 *   • The autocomplete subsystem. `.dcg-autocomplete__dropdown` IS rendered
 *     (see _patchAutocomplete) and the outside-click handler IS bound, so it
 *     is reachable. Only the `autocomplete-input` case is handled without ever
 *     being emitted — one unreachable switch arm, not a dead subsystem.
 *
 *   • The recommendations pipeline. `this.state.recommendations` really is
 *     write-only — assigned at three sites, read at none — so the Scryfall and
 *     BinderPOS calls inside _loadRecommendations produce discarded data.
 *     BUT _patchRecDOM(), the only thing that patches recommendation cards
 *     into the DOM, is called exclusively from inside that loader, and it
 *     renders from card._alternatives (populated by _loadAlternativePrintings).
 *     Deleting the loader would stop rec cards ever updating. That is a
 *     refactor with a behaviour change, not a dead-code cut — left intact.
 *
 * KNOWN REMAINING WASTE (not dead code, but real cost):
 *   • N+1 fetches: up to ~71 requests per card on the Shopify path.
 *   • The discarded recommendations data described above.
 *   • Document-level keydown listener leaked per verify-modal re-render.
 */
(function () {
  'use strict';

  (window.DCG = window.DCG || {}).decklistVersion = '2.2';

  /* ============================================================
     GAME SEARCH RULES
  ============================================================ */
  const GAME_SEARCH_CONFIG = {
    mtg:           { useBinderPos: true,  productType: null,                          strictSetMatch: false, hasPitchInTitle: false },
    pokemon:       { useBinderPos: false, productType: 'Pokemon Single',              strictSetMatch: false, hasPitchInTitle: false },
    fleshAndBlood: { useBinderPos: false, productType: 'Flesh And Blood Single',      strictSetMatch: false, hasPitchInTitle: true  },
    one:           { useBinderPos: false, productType: 'One Piece Single',            strictSetMatch: true,  hasPitchInTitle: false },
    yugioh:        { useBinderPos: false, productType: 'Yugioh Single',               strictSetMatch: false, hasPitchInTitle: false },
    lor:           { useBinderPos: false, productType: 'Lorcana Single',              strictSetMatch: false, hasPitchInTitle: false },
    dgm:           { useBinderPos: false, productType: 'Digimon Single',              strictSetMatch: false, hasPitchInTitle: false },
    swu:           { useBinderPos: false, productType: 'Star Wars: Unlimited Single', strictSetMatch: false, hasPitchInTitle: false },
    gar:           { useBinderPos: false, productType: 'Grand Archive Single',        strictSetMatch: false, hasPitchInTitle: false },
    fow:           { useBinderPos: false, productType: 'Force of Will Single',        strictSetMatch: false, hasPitchInTitle: false },
    riftbound:     { useBinderPos: false, productType: 'Riftbound Single',            strictSetMatch: false, hasPitchInTitle: false },
  };

  /* ============================================================
     GAME CONFIGS
  ============================================================ */
  const GAME_CONFIGS = {
    mtg: {
      label: 'Magic: The Gathering',
      formats: ['Standard', 'Pioneer', 'Modern', 'Legacy', 'Vintage', 'Commander / EDH', 'Pauper', 'Draft / Sealed'],
      parser: 'generic',
      placeholder: 'Paste your decklist (MTG Goldfish / Moxfield format):\n\n4 Lightning Bolt\n4 Mountain\n2 Goblin Guide',
    },
    pokemon: {
      label: 'Pokémon TCG',
      formats: ['Standard', 'Expanded', 'Unlimited'],
      parser: 'pokemon',
      placeholder: 'Paste your decklist (PTCG Live / Limitless format):\n\nPokémon: 12\n4 Charizard ex OBF 125\n\nTrainer: 38\n4 Arven SSP 186\n\nEnergy: 10\n10 Fire Energy',
    },
    fab: {
      label: 'Flesh & Blood (Beta)',
      formats: ['Classic Constructed', 'Blitz', 'Draft', 'Sealed'],
      parser: 'fab',
      placeholder: 'Paste your decklist — FaBrary or FaBDB format:\n\n3x Cut from the Same Cloth (red)\n1x Blacktek Whisperers',
    },
    yugioh: {
      label: 'Yu-Gi-Oh!',
      formats: ['Advanced', 'Traditional', 'Speed Duel'],
      parser: 'generic',
      placeholder: 'Paste your decklist:\n\n3 Dark Magician\n3 Blue-Eyes White Dragon',
    },
    lorcana: {
      label: 'Lorcana',
      formats: ['Core Constructed', 'Sealed'],
      parser: 'generic',
      placeholder: 'Paste your decklist (Pixelborn format):\n\n4 Elsa - Spirit of Winter',
    },
    starwars: {
      label: 'Star Wars: Unlimited',
      formats: ['Premier', 'Twin Suns', 'Sealed'],
      parser: 'generic',
      placeholder: 'Paste your decklist:\n\n3 Han Solo\n2 Chewbacca',
    },
    onepiece: {
      label: 'One Piece TCG (Beta)',
      formats: ['Standard'],
      parser: 'onepiece',
      placeholder: 'Paste your decklist (Limitless format):\n\nLeader\n1 Trafalgar Law (OP09-001)\nCharacter (43)\n4 Sugar (OP10-065)',
    },
    digimon: {
      label: 'Digimon TCG',
      formats: ['Standard'],
      parser: 'generic',
      placeholder: 'Paste your decklist:\n\n4 Agumon BT1-010\n3 Greymon BT1-013',
    },
    _default: {
      label: 'Trading Card Game',
      formats: [],
      parser: 'generic',
      placeholder: 'Paste your decklist:\n\n4 Card Name\n2 Another Card',
    },
  };

  const LIVE_GAME_IDS    = new Set(['mtg', 'pokemon', 'fleshAndBlood', 'one']);
  const SUPPRESSED_GAME_IDS = new Set(['pkms']);
  const GAME_ID_MAP = {
    'mtg': 'mtg', 'pokemon': 'pokemon', 'pkms': 'pokemon',
    'fleshAndBlood': 'fab', 'one': 'onepiece', 'dgm': 'digimon',
    'fow': 'fow', 'gar': 'gar', 'lor': 'lorcana', 'swu': 'starwars', 'yugioh': 'yugioh',
  };
  const EXTRA_LABELS = { 'fow': 'Force of Will', 'gar': 'Grand Archive' };

  const MTG_BASIC_LANDS = new Set([
    'plains', 'island', 'swamp', 'mountain', 'forest', 'wastes',
    'snow-covered plains', 'snow-covered island', 'snow-covered swamp',
    'snow-covered mountain', 'snow-covered forest',
  ]);

  function isGameLive(game) { return LIVE_GAME_IDS.has(game.gameId); }

  function deduplicateGames(games) {
    const seen = new Set(), result = [];
    games.forEach(game => {
      if (SUPPRESSED_GAME_IDS.has(game.gameId)) return;
      const canonicalKey = GAME_ID_MAP[game.gameId] || game.gameId;
      if (seen.has(canonicalKey)) return;
      seen.add(canonicalKey);
      const cfg = GAME_CONFIGS[canonicalKey];
      result.push({
        ...game,
        _displayName: cfg ? cfg.label : (EXTRA_LABELS[game.gameId] || game.gameName || game.gameId),
        _isLive: isGameLive(game),
      });
    });
    const liveOrder = ['mtg', 'pokemon', 'fleshAndBlood', 'one'];
    result.sort((a, b) => {
      if (a._isLive && !b._isLive) return -1;
      if (!a._isLive && b._isLive) return 1;
      if (a._isLive && b._isLive) {
        const ia = liveOrder.indexOf(a.gameId), ib = liveOrder.indexOf(b.gameId);
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
      }
      return a._displayName.localeCompare(b._displayName);
    });
    return result;
  }

  /* ============================================================
     PARSERS
  ============================================================ */
  const Parsers = {

    generic(text) {
      const lines = text.split('\n');
      const cardMap = {};
      lines.forEach(rawLine => {
        const line = rawLine.trim();
        if (!line || line.startsWith('//') || line.startsWith('#')) return;
        if (/^(sideboard|commander|companion|deck|maybeboard)$/i.test(line)) return;
        if (/^(Pokémon|Pokemon|Trainer|Energy|Total):/i.test(line)) return;
        const match = line.match(/^(\d+)[x\s]+(.+)$/);
        let cardName, qty;
        if (match) { qty = parseInt(match[1], 10); cardName = match[2].trim(); }
        else { qty = 1; cardName = line; }
        cardName = cardName.replace(/\s*\([^)]+\)\s*$/, '').trim();
        if (!cardName) return;
        if (cardMap[cardName]) cardMap[cardName].quantity += qty;
        else cardMap[cardName] = { card: cardName, quantity: qty };
      });
      return Object.values(cardMap);
    },

    pokemon(text) {
      const lines = text.split('\n');
      const cardMap = {};
      lines.forEach(rawLine => {
        const line = rawLine.trim();
        if (!line) return;
        if (/^(Pokémon|Pokemon|Trainer|Energy|Total)/i.test(line)) return;
        const match = line.match(/^(\d+)\s+(.+)$/);
        if (!match) return;
        const qty = parseInt(match[1], 10);
        let cardName = match[2].trim();
        let setCode = null, collectorNum = null;
        const setMatch = cardName.match(/\s+([A-Z]{2,4})\s+(\d+)$/);
        if (setMatch) {
          setCode = setMatch[1];
          collectorNum = setMatch[2];
          cardName = cardName.replace(/\s+[A-Z]{2,4}\s+\d+$/, '').trim();
        }
        if (!cardName) return;
        if (cardMap[cardName]) cardMap[cardName].quantity += qty;
        else cardMap[cardName] = { card: cardName, quantity: qty, setCode, collectorNum };
      });
      return Object.values(cardMap);
    },

    onepiece(text) {
      const lines = text.split('\n');
      const cardMap = {};
      const SECTION = /^(leader|character|event|stage|don!?)(?:\s*\(\d+\))?$/i;
      lines.forEach(rawLine => {
        const line = rawLine.trim();
        if (!line || SECTION.test(line)) return;
        const m = line.match(/^(\d+)\s+(.+?)\s+\(([A-Z0-9]+-\d+)\)\s*$/);
        if (m) {
          const qty = parseInt(m[1], 10), name = m[2].trim(), setCode = m[3];
          const key = `${name}::${setCode}`;
          if (cardMap[key]) cardMap[key].quantity += qty;
          else cardMap[key] = { card: name, quantity: qty, setCode };
          return;
        }
        const fallback = line.match(/^(\d+)\s+(.+)$/);
        if (fallback) {
          const qty = parseInt(fallback[1], 10), name = fallback[2].trim();
          if (cardMap[name]) cardMap[name].quantity += qty;
          else cardMap[name] = { card: name, quantity: qty, setCode: null };
        }
      });
      return Object.values(cardMap);
    },

    fab(text) {
      const lines = text.split('\n');
      const cardMap = {};
      const SKIP = /^(name:|hero:|format:|arena cards|deck cards|made with|see the full deck)/i;
      lines.forEach(rawLine => {
        const line = rawLine.trim();
        if (!line || line.startsWith('//') || line.startsWith('http') || SKIP.test(line)) return;
        const fabrary = line.match(/^(\d+)x\s+(.+)$/i);
        if (fabrary) {
          const qty = parseInt(fabrary[1], 10);
          let name = fabrary[2].trim();
          name = name.replace(/\s+\((red|blue|yellow|purple)\)\s*$/i, (m, c) =>
            ` (${c.charAt(0).toUpperCase() + c.slice(1)})`);
          if (!name) return;
          if (cardMap[name]) cardMap[name].quantity += qty;
          else cardMap[name] = { card: name, quantity: qty };
          return;
        }
        const fabdb = line.match(/^\((\d+)\)\s+(.+?)(?:\s+\([A-Z]{2,4}\))?$/);
        if (fabdb) {
          const qty = parseInt(fabdb[1], 10), name = fabdb[2].trim();
          if (!name) return;
          if (cardMap[name]) cardMap[name].quantity += qty;
          else cardMap[name] = { card: name, quantity: qty };
          return;
        }
        const generic = line.match(/^(\d+)\s+(.+)$/);
        if (generic) {
          const qty = parseInt(generic[1], 10);
          const name = generic[2].replace(/\s*\([^)]+\)\s*$/, '').trim();
          if (!name) return;
          if (cardMap[name]) cardMap[name].quantity += qty;
          else cardMap[name] = { card: name, quantity: qty };
        }
      });
      return Object.values(cardMap);
    },
  };

  /* ============================================================
     API
  ============================================================ */
  const API_BASE = 'https://api.binderpos.com/external/shopify';

  const Api = {
    /* ── DCG inventory endpoint — v1.5, 03 AUG 2026 ─────────────────────
       Single request that returns matches WITH exact per-variant
       inventory_quantity, read from Liquid via
       templates/search.inventory.liquid.

       This is the ONLY storefront source of true quantities:
         BinderPOS forStore ..... 1/0 only (220 entries, all 0, measured)
         /products/{handle}.js .. omits inventory_quantity entirely
         /variants/{id}.json .... 404, endpoint does not exist
       Verified against the Admin API — Wingull 15/6, Dreepy 2/1, exact.

       Returns null when the endpoint is unavailable so the caller can fall
       back. Shopify serves the DEFAULT search template with status 200 when
       a ?view= template is missing, so a JSON.parse failure means
       "not deployed", NEVER "no results" — hence the dcg_endpoint check. */
    async searchInventory(term, productType) {
      /* Scope the search to the selected game — DCG 03 AUG 2026.
         Shopify storefront search accepts field filters inline, so
         `Special Red Card product_type:"Pokemon Single"` is honoured
         server-side. Measured on this store:

           plain "Special Red Card" ...... 20 results, 0 Pokemon,
                                           the card NOT among them
           + product_type filter ......... 2 results, both Pokemon,
                                           the card found

         Relevance was spending every one of the 20 slots on Yu-Gi-Oh and
         Flesh and Blood cards matching "Red", so a card the store genuinely
         stocks was invisible. Filtering server-side also shrinks the payload
         by roughly an order of magnitude. */
      const fetchOnce = async (q) => {
        const res = await fetch(
          `/search?q=${encodeURIComponent(q)}&type=product&view=inventory`
        );
        if (!res.ok) return null;
        const text = await res.text();
        let data = null;
        try { data = JSON.parse(text); } catch (e) { return null; }
        if (!data || data.dcg_endpoint !== 'search.inventory') return null;
        if (!Array.isArray(data.products)) return null;
        return data;
      };
      try {
        if (productType) {
          const scoped = await fetchOnce(`${term} product_type:"${productType}"`);
          /* Only fall back to an unscoped search if the scoped one returned
             nothing at all — covers products with a missing or non-standard
             product_type. The caller still applies its own type filter, so
             cross-game results from the fallback are discarded rather than
             shown. */
          if (scoped && scoped.products.length > 0) return scoped;
        }
        return await fetchOnce(term);
      } catch (e) { return null; }
    },

    fetchGames(domain) {
      return fetch(`${API_BASE}/supportedGames?storeUrl=${encodeURIComponent(domain)}`).then(r => r.json());
    },
    fetchConditions(domain) {
      return fetch(`${API_BASE}/conditions?storeUrl=${encodeURIComponent(domain)}`).then(r => r.json());
    },
    searchDecklist(cards, domain, gameId) {
      return fetch(`${API_BASE}/decklist?storeUrl=${encodeURIComponent(domain)}&type=${encodeURIComponent(gameId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(cards),
      }).then(r => r.json());
    },


    async searchDecklistViaShopify(cards, gameId) {
      const BATCH = 4;
      const results = [];

      const ONE_PIECE_SET_NAMES = {
        'OP01':'Romance Dawn','OP02':'Paramount War','OP03':'Pillars of Strength',
        'OP04':'Kingdoms of Intrigue','OP05':'Awakening of the New Era','OP06':'Wings of the Captain',
        'OP07':'Five Hundred Years in the Future','OP08':'Two Legends',
        'OP09':'Emperors in the New World','OP10':'Royal Blood','OP11':'The Reunion',
        'OP12':'Legacy of the Master','OP13':'Super Rookies','OP14':'Bonds of Three Brothers','OP15':'Adventure on the Island of the Gods',
        'ST01':'Straw Hat Crew','ST02':'Worst Generation','ST03':'The Seven Warlords of the Sea',
        'ST04':'Animal Kingdom Pirates','ST05':'Film Edition','ST06':'Absolute Justice',
        'ST07':'Big Mom Pirates','ST08':'Monkey D. Luffy','ST09':'Yamato',
        'ST10':'The Seven Warlords of the Sea','ST11':'Uta','ST12':'Zoro & Sanji',
        'ST13':'The Three Captains','ST14':'Germa 66','ST15':'Red-Haired Shanks',
        'ST16':'Uta','ST17':'OVA Edition','ST18':'Impel Down','ST19':'Impel Down',
        'ST20':'One Piece Film: Red','EB01':'Extra Booster Memorial Collection',
        'EB02':'Extra Booster Memorial Collection',
        'EB03':'Extra Booster: One Piece Heroines Edition',
        'EB04':"The Azure Sea's Seven",'P':null,
      };

      const PTCG_SET_NAMES = {
        'SSP':'Surging Sparks','SCR':'Stellar Crown','SFA':'Shrouded Fable',
        'TWM':'Twilight Masquerade','TEF':'Temporal Forces','PAF':'Paldean Fates',
        'OBF':'Obsidian Flames','PAL':'Paldea Evolved','SVI':'Scarlet & Violet',
        'ASC':'Ascended Heroes','PRE':'Prismatic','DRI':'Destined Rivals','JTG':'Journey Together',
        'CRZ':'Crown Zenith','SIT':'Silver Tempest','LOR':'Lost Origin','PGO':'Pokemon GO',
        'ASR':'Astral Radiance','BRS':'Brilliant Stars','FST':'Fusion Strike','EVS':'Evolving Skies',
        'CRE':'Chilling Reign','BST':'Battle Styles','SHF':'Shining Fates','VIV':'Vivid Voltage',
        'DAA':'Darkness Ablaze','RCL':'Rebel Clash','SSH':'Sword & Shield','PAR':'Paradox Rift',
        'MEG':'Mega Evolution','MEE':null,'PFL':null,
      };

      const total = cards.length;
      let processed = 0;
      const progressEl = document.querySelector('.dcg-dl__progress');
      const updateProgress = () => { if (progressEl) progressEl.textContent = `Searching ${processed}/${total} cards…`; };
      updateProgress();

      for (let i = 0; i < cards.length; i += BATCH) {
        const batch = cards.slice(i, i + BATCH);
        const batchResults = await Promise.all(batch.map(async ({ card, quantity, setCode, collectorNum }) => {
          try {
            const gameConfig = GAME_SEARCH_CONFIG[gameId] || {};
            const expectedType = gameConfig.productType || null;
            const parenMatch = card.match(/^(.+?)\s+(\([^)]+\))\s*$/);
            const searchTerm = parenMatch ? parenMatch[1].trim() : card;
            let titleFilter = null;
            if (parenMatch) {
              titleFilter = parenMatch[2].toLowerCase();
            } else if (setCode) {
              if (gameId === 'one') {
                const isPromo = /^P-/i.test(setCode);
                if (isPromo) {
                  titleFilter = `(${setCode.toLowerCase()})`;
                } else {
                  const prefix = setCode.match(/^([A-Z]+\d*)/)?.[1];
                  const setName = ONE_PIECE_SET_NAMES[prefix];
                  titleFilter = setName ? setName.toLowerCase() : null;
                }
              } else {
                const ptcgSetName = PTCG_SET_NAMES[setCode];
                titleFilter = ptcgSetName !== undefined
                  ? (ptcgSetName ? ptcgSetName.toLowerCase() : null)
                  : setCode.toLowerCase();
              }
            }
            const isStrictGame = gameConfig.strictSetMatch || false;

            /* ── Fast path: the inventory endpoint ────────────────────────
               Replaces, per card: 1 suggest.json + up to 10 product.js +
               up to ~50 /variants/ calls (all 404). Measured on a 60-card
               list before this change: 564 requests / 4.3 MB / 41 seconds,
               of which 105 were /variants/ 404s.

               Everything downstream is unchanged — this only swaps how
               `searchProducts` and `fullProducts` are populated, so the
               name filter, titleFilter, skuPrefix, pokemonNumFilter and
               matched/unmatched logic all still apply exactly as before. */
            let searchProducts = [];
            let fullProducts = null;

            const dcgInv = await Api.searchInventory(searchTerm, expectedType);
            if (dcgInv) {
              searchProducts = dcgInv.products.map(p => ({
                title: p.title, image: { url: p.image || '' },
              }));
              if (searchProducts.length === 0) {
                return { searchName: setCode ? `${card} (${setCode})` : card, displayName: card,
                         setCode, requested: quantity, found: 0, validName: false, products: [] };
              }
              const norm = str => (str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
              const et = (expectedType || '').toLowerCase();
              const GAME_VENDORS_EP = {
                'one piece single': ['bandai'], 'pokemon single': ['pokemon', 'pok\u00e9mon'],
                'flesh and blood single': ['legend story studios', 'lss', 'flesh and blood'],
                'yugioh single': ['konami', 'yu-gi-oh'], 'lorcana single': ['ravensburger', 'disney'],
                'digimon single': ['bandai', 'digimon'],
              };
              fullProducts = dcgInv.products.filter(p => {
                if (!expectedType) return true;
                const pt = (p.type || '').trim().toLowerCase();
                if (pt) return pt.replace(/s$/, '') === et.replace(/s$/, '');
                /* No type set — same vendor/tag heuristic the legacy path uses,
                   plus the game name itself, since this store's vendors are
                   game names (Digimon, Yu-Gi-Oh!, Disney) not publishers. */
                const vendor = norm(p.vendor);
                const known = (GAME_VENDORS_EP[et] || []).concat(et.replace(/\s+single$/, ''));
                return known.some(v => vendor.includes(norm(v)));
              }).map(p => ({
                title: p.title,
                img: p.image || '',
                variants: (p.variants || []).map(v => ({
                  shopifyId: String(v.id),
                  title: v.title,
                  sku: (v.sku || '').toUpperCase(),
                  price: v.price / 100,
                  /* Tracked variants report an exact count. Untracked ones
                     report 0 in Liquid, which is indistinguishable from
                     genuinely zero — fall back to the legacy conservative 1
                     so an untracked-but-available variant is not hidden. */
                  quantity: v.management ? Math.max(0, v.qty) : (v.available ? 1 : 0),
                })),
              }));
            }

            /* ── Fallback: legacy pipeline, if the endpoint is not deployed ── */
            if (fullProducts === null) {
            const searchUrl = `/search/suggest.json?q=${encodeURIComponent(searchTerm)}&type=product&limit=10&resources[type]=product`;
            const searchRes = await fetch(searchUrl);
            if (!searchRes.ok) throw new Error(`Search failed: ${searchRes.status}`);
            const searchData = await searchRes.json();
            searchProducts = searchData?.resources?.results?.products || [];
            if (searchProducts.length === 0) {
              return { searchName: setCode ? `${card} (${setCode})` : card, displayName: card,
                       setCode, requested: quantity, found: 0, validName: false, products: [] };
            }
            fullProducts = (await Promise.all(
              searchProducts.slice(0, 10).map(async p => {
                const handle = p.url?.split('/products/')[1]?.split('?')[0];
                if (!handle) return null;
                try {
                  const pRes = await fetch(`/products/${handle}.js`);
                  if (!pRes.ok) return null;
                  const pData = await pRes.json();
                  if (expectedType) {
                    /* DCG 03 AUG 2026 — root cause of F-DECK-4's knock-on bug.
                       Shopify's /products/{handle}.js exposes the product type
                       as `type`, NOT `product_type` (verified live: the payload
                       has no product_type key at all). Reading product_type
                       always yielded undefined, so `pt` was always '' and EVERY
                       game fell through to the vendor/tag heuristic below.

                       That heuristic passed Pokemon and One Piece only by
                       coincidence — their vendors are 'Pokémon' and 'Bandai',
                       which happen to be in GAME_VENDORS. Flesh and Blood's
                       vendor here is 'Flesh And Blood' while the map looks for
                       'legend story studios'/'lss', so every F&B card was
                       rejected and reported as out of stock.

                       Reading `type` restores the exact match for all games and
                       demotes the heuristic to a genuine fallback for products
                       with no type set. It also stops sealed product leaking
                       into decklist results: 'Pokemon Sealed' shares the vendor
                       'Pokémon' and previously passed the vendor check.
                       product_type kept as a defensive second read. */
                    const pt = (pData.type || pData.product_type || '').trim().toLowerCase();
                    const et = expectedType.toLowerCase();
                    const ptCore = pt.replace(/s$/, '');
                    const etCore = et.replace(/s$/, '');
                    if (pt && ptCore !== etCore) return null;
                    if (!pt) {
                      const normalise = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                      const vendor = normalise(pData.vendor || '');
                      const GAME_VENDORS = {
                        'one piece single': ['bandai'], 'pokemon single': ['pokemon', 'pokémon'],
                        'flesh and blood single': ['legend story studios', 'lss'],
                        'yugioh single': ['konami'], 'lorcana single': ['ravensburger'],
                        'digimon single': ['bandai'],
                      };
                      const knownVendors = GAME_VENDORS[et] || [];
                      const vendorMatch = knownVendors.some(v => vendor.includes(normalise(v)));
                      const rawTags = pData.tags;
                      const tagsStr = Array.isArray(rawTags) ? rawTags.join(' ') : (rawTags || '');
                      const tags = normalise(tagsStr);
                      const gameTag = et.split(' ')[0];
                      const tagMatch = tags.includes(gameTag);
                      if (!vendorMatch && !tagMatch) return null;
                    }
                  }
                  const productImg = pData.featured_image || p.image?.url || (pData.images?.[0]) || '';
                  const rawVariants = pData.variants || [];
                  const variantCounts = await Promise.all(rawVariants.map(async v => {
                    const rawQty = v.inventory_quantity;
                    if (rawQty !== undefined && rawQty !== null) return rawQty > 0 ? rawQty : 0;
                    if (!v.available) return 0;
                    try {
                      const vRes = await fetch(`/variants/${v.id}.json`);
                      if (vRes.ok) {
                        const vData = await vRes.json();
                        // BinderPOS wraps the variant under "product_variant", not "variant"
                        const realQty = vData?.product_variant?.inventory_quantity
                                     ?? vData?.variant?.inventory_quantity;
                        if (realQty !== undefined && realQty !== null) return Math.max(0, realQty);
                      }
                    } catch {}
                    // BinderPOS does not expose inventory_quantity — v.available is true but
                    // count is unknown. Return 1 (conservative) rather than the player's
                    // requested deck count, which caused the badge to mirror the decklist.
                    return 1;
                  }));
                  const productVariants = rawVariants.map((v, i) => ({
                    shopifyId: String(v.id), title: v.title,
                    sku: (v.sku || '').toUpperCase(),
                    price: v.price / 100, quantity: variantCounts[i],
                  }));
                  return { title: pData.title, img: productImg, variants: productVariants };
                } catch { return null; }
              })
            )).filter(Boolean);
            } /* end legacy fallback */

            if (fullProducts.length === 0) {
              /* DCG 03 AUG 2026 — cross-game thumbnail leak.
                 Was searchProducts[0]?.image?.url, i.e. the first UNFILTERED
                 search result. We only reach here when fullProducts is empty,
                 meaning nothing survived the product-type filter — so by
                 definition there is no correct image. Searching "Special Red
                 Card" returns 20 products, none of them Pokemon (5 Flesh and
                 Blood, 13 Yu-Gi-Oh, 2 Digimon), and this rendered a Flesh and
                 Blood card's art on a missing Pokemon trainer. No image beats
                 the wrong game's card. */
              const fallbackImg = '';
              return { searchName: setCode ? `${card} (${setCode})` : card, displayName: card,
                       setCode, requested: quantity, found: 0, validName: true, products: [],
                       imageUrl: fallbackImg };
            }

            const searchTermLower = searchTerm.toLowerCase();
            function startsWithName(title, name) {
              return title === name || title.startsWith(name + ' ') || title.startsWith(name + '(') ||
                     title.startsWith(name + '[') || title.startsWith(name + ':') || title.startsWith(name + ',');
            }
            const nameFiltered = fullProducts.filter(p => startsWithName(p.title.toLowerCase(), searchTermLower));
            const nameFilteredProducts = nameFiltered.length > 0 ? nameFiltered : fullProducts;
            let filteredByTitle = nameFilteredProducts;
            if (titleFilter) {
              const byTitle = nameFilteredProducts.filter(p => p.title.toLowerCase().includes(titleFilter));
              if (byTitle.length > 0) filteredByTitle = byTitle;
            }
            const inStockProducts = filteredByTitle.filter(p => p.variants.some(v => v.quantity > 0));
            const matched = [], unmatched = [];
            const skuPrefix = (gameId === 'one' && setCode) ? setCode.toUpperCase() : null;
            const pokemonNumFilter = (gameId === 'pokemon' && collectorNum)
              ? `(${String(parseInt(collectorNum)).padStart(3, '0')}/` : null;

            inStockProducts.forEach(p => {
              if (skuPrefix) {
                p.variants.some(v => v.sku && v.sku.toUpperCase().includes(skuPrefix))
                  ? matched.push(p) : unmatched.push(p);
              } else if (pokemonNumFilter) {
                p.title.toLowerCase().includes(pokemonNumFilter)
                  ? matched.push(p) : unmatched.push(p);
              } else {
                (!titleFilter || p.title.toLowerCase().includes(titleFilter))
                  ? matched.push(p) : unmatched.push(p);
              }
            });

            let useMatched, useUnmatched;
            if (titleFilter && isStrictGame) {
              useMatched = matched; useUnmatched = unmatched;
            } else if (pokemonNumFilter) {
              useMatched = matched; useUnmatched = unmatched;
            } else if (matched.length > 0) {
              useMatched = matched; useUnmatched = unmatched;
            } else {
              useMatched = inStockProducts; useUnmatched = [];
            }

            // Fallback search for One Piece and Pokemon
            if ((gameId === 'one' || gameId === 'pokemon') && useMatched.length === 0 && (setCode || collectorNum)) {
              const isPromo = /^P-/i.test(setCode);
              let fallbackWord = null;
              if (isPromo) {
                fallbackWord = setCode;
              } else if (gameId === 'one') {
                const pfx = setCode.match(/^([A-Z]+\d*)/)?.[1];
                const sn = ONE_PIECE_SET_NAMES[pfx];
                fallbackWord = sn?.split(' ').find(w => {
                  const clean = w.replace(/[^a-zA-Z]/g, '');
                  return clean.length > 3 && !['Piece','Booster','Extra','Edition'].includes(clean);
                }) || null;
              } else if (gameId === 'pokemon' && collectorNum) {
                fallbackWord = String(parseInt(collectorNum)).padStart(3, '0');
              }
              if (fallbackWord) {
                try {
                  const fbUrl = `/search/suggest.json?q=${encodeURIComponent(searchTerm + ' ' + fallbackWord)}&type=product&limit=10&resources[type]=product`;
                  const fbRes = await fetch(fbUrl);
                  if (fbRes.ok) {
                    const fbData = await fbRes.json();
                    const fbProds = fbData?.resources?.results?.products || [];
                    const fbFull = (await Promise.all(fbProds.slice(0, 10).map(async p => {
                      const h = p.url?.split('/products/')[1]?.split('?')[0];
                      if (!h) return null;
                      try {
                        const r = await fetch(`/products/${h}.js`);
                        if (!r.ok) return null;
                        const d = await r.json();
                        if (expectedType) {
                          /* see note at the primary type check: the field is `type`, not `product_type` */
                          const pt = (d.type || d.product_type || '').trim().toLowerCase();
                          const et = expectedType.toLowerCase();
                          if (pt && pt.replace(/s$/,'') !== et.replace(/s$/,'')) return null;
                        }
                        const img2 = d.featured_image || p.image?.url || '';
                        return {
                          title: d.title, img: img2,
                          variants: await Promise.all((d.variants || []).map(async v => {
                            const rawQty = v.inventory_quantity;
                            let qty;
                            if (rawQty !== undefined && rawQty !== null) qty = rawQty > 0 ? rawQty : 0;
                            else if (!v.available) qty = 0;
                            else {
                              try {
                                const vr = await fetch(`/variants/${v.id}.json`);
                                if (vr.ok) {
                                  const vd = await vr.json();
                                  // BinderPOS wraps under "product_variant", not "variant"
                                  const rq = vd?.product_variant?.inventory_quantity
                                          ?? vd?.variant?.inventory_quantity;
                                  qty = (rq !== undefined && rq !== null) ? Math.max(0, rq) : 1;
                                } else qty = 1;
                              } catch { qty = 1; }
                            }
                            return { shopifyId: String(v.id), title: v.title, sku: (v.sku||'').toUpperCase(), price: v.price/100, quantity: qty };
                          })),
                        };
                      } catch { return null; }
                    }))).filter(Boolean);
                    const fbMatched = fbFull.filter(p => {
                      if (skuPrefix) return p.variants.some(v => v.sku && v.sku.toUpperCase().includes(skuPrefix));
                      if (pokemonNumFilter) return p.title.toLowerCase().includes(pokemonNumFilter);
                      return false;
                    });
                    fbMatched.forEach(p => { if (!useMatched.find(m => m.title === p.title)) useMatched.push(p); });
                  }
                } catch {}
              }
            }

            const totalMatched = useMatched.reduce((s, p) => s + p.variants.reduce((vs, v) => vs + v.quantity, 0), 0);
            const exactSetMatch = !titleFilter || matched.length > 0;
            /* Prefer a same-name printing for the thumbnail; only fall back to
               the raw search result if the name filter found nothing at all,
               otherwise a missing card could display an unrelated card's art. */
            const anyImg = (useMatched[0] || useUnmatched[0] || nameFiltered[0] || fullProducts[0])?.img || '';
            /* DCG 03 AUG 2026 — second instance of the alternates leak.
               Was `fullProducts`, the search result before the name filter, so
               when nothing matched this offered EVERY in-stock product the
               search returned as an "alternative printing". Searching
               "Greninja ex" surfaced Dragonite EX, Jirachi, Mewtwo EX and
               Alolan Exeggutor GX — all matching only on "ex"/"GX".

               `nameFiltered` is the strict startsWithName() set, so alternates
               are now genuine printings of the requested card. Note this also
               correctly excludes "Mega Greninja ex" and plain "Greninja":
               neither title starts with "Greninja ex", and they are different
               cards, not alternate printings of the same one. */
            const noStockAlternatives = totalMatched === 0
              ? nameFiltered.filter(p => p.variants.some(v => v.quantity > 0) && !useMatched.includes(p))
              : [];

            return {
              searchName: setCode ? `${card} (${setCode})` : card,
              displayName: card, setCode, requested: quantity,
              found: totalMatched > 0 ? 1 : 0, validName: true,
              products: useMatched, alternativeProducts: useUnmatched,
              noStockAlternatives, imageUrl: anyImg, exactSetMatch,
              /* Alternate printings pool — used for partially-fulfilled and
                 missing cards (e.g. other Iono printings when PAL Cosmo Foil
                 has only 1 and the deck needs 4).

                 DCG 03 AUG 2026 — was `fullProducts`, which is the search
                 result AFTER the product-type filter but BEFORE the name
                 filter. Shopify relevance for "Greninja ex" also returns
                 Dragonite EX, Charizard EX, Jirachi EX and plain Greninja —
                 they match on "ex" — and the consumer at the alternates site
                 filtered only on "not already matched" and "has stock", never
                 on the name. Unrelated cards were therefore offered as
                 "Alternative Printings".

                 `nameFiltered` is the STRICT set: startsWithName() against the
                 search term, without the `.length > 0 ? … : fullProducts`
                 fallback that nameFilteredProducts applies. If nothing matches
                 the name there are genuinely no alternate printings, and an
                 empty pool is the correct answer. */
              _allSearchProducts: nameFiltered,
            };
          } catch (err) {
            console.log('[DCG] ERROR for', card, ':', err.message);
            return { searchName: setCode ? `${card} (${setCode})` : card, displayName: card,
                     setCode, requested: quantity, found: 0, validName: false, products: [] };
          }
        }));
        processed += batch.length;
        updateProgress();
        results.push(...batchResults);
      }
      if (progressEl) progressEl.textContent = '';
      return results;
    },
  };

  /* ============================================================
     CART
  ============================================================ */
  const Cart = {
    update(items) {
      const updates = {};
      items.forEach(item => { updates[item.shopifyId] = item.quantityUsed; });
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ updates }),
      }).then(r => r.json());
    },
    // Accepts a raw { variantId: quantity } map — used for snapshot restore
    rawUpdate(updates) {
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ updates }),
      }).then(r => r.json());
    },
  };

  /* ============================================================
     SCRYFALL API  (MTG only)
  ============================================================ */
  const Scryfall = {
    _cache: {},
    async getCard(name) {
      const key = name.toLowerCase();
      if (this._cache[key]) return this._cache[key];
      try {
        const res = await fetch(`https://api.scryfall.com/cards/named?fuzzy=${encodeURIComponent(name)}`);
        if (!res.ok) return null;
        const data = await res.json();
        this._cache[key] = data;
        return data;
      } catch { return null; }
    },
    formatToLegal(format) {
      if (!format) return null;
      const map = {
        'standard':'standard','pioneer':'pioneer','modern':'modern','legacy':'legacy',
        'vintage':'vintage','commander / edh':'commander','commander':'commander',
        'edh':'commander','pauper':'pauper','draft / sealed':null,'draft':null,'sealed':null,
      };
      return map[format.toLowerCase()] || null;
    },
    async getSimilarNames(cardName, format = null, limit = 8) {
      const card = await this.getCard(cardName);
      if (!card) return [];
      const parts = [];
      const types = (card.type_line || '').split('—')[0].trim().split(' ');
      const primaryType = types.find(t =>
        ['Creature','Instant','Sorcery','Enchantment','Artifact','Planeswalker','Land','Battle'].includes(t));
      if (primaryType) parts.push(`t:${primaryType}`);
      if (card.color_identity?.length > 0) parts.push(`id<=${card.color_identity.join('')}`);
      if (card.cmc != null && primaryType !== 'Land') {
        const cmc = Math.round(card.cmc);
        parts.push(`cmc>=${Math.max(0, cmc - 1)} cmc<=${cmc + 1}`);
      }
      const legal = this.formatToLegal(format);
      parts.push(legal ? `legal:${legal}` : '-is:digital');
      parts.push(`-!"${cardName}"`);
      try {
        const res = await fetch(`https://api.scryfall.com/cards/search?q=${encodeURIComponent(parts.join(' '))}&order=edhrec&limit=${limit}`);
        if (!res.ok) return [];
        const data = await res.json();
        return (data.data || []).map(c => c.name).slice(0, limit);
      } catch { return []; }
    },
  };

  /* ============================================================
     RECOMMENDATIONS ENGINE
  ============================================================ */
  const Recommendations = {
    _cache: {},
    async getForCard(cardName, gameId, domain, conditions, storeConditions, format = null) {
      const cacheKey = `${gameId}:${format || 'any'}:${cardName}`;
      if (this._cache[cacheKey]) return this._cache[cacheKey];
      const isMtg = gameId === 'mtg';
      if (isMtg && MTG_BASIC_LANDS.has(cardName.toLowerCase())) {
        const result = { isBasicLand: true, notFoundMatch: false, alternatives: [] };
        this._cache[cacheKey] = result;
        return result;
      }
      if (!isMtg) {
        const result = { isBasicLand: false, notFoundMatch: false, alternatives: [] };
        this._cache[cacheKey] = result;
        return result;
      }
      try {
        const similarNames = await Scryfall.getSimilarNames(cardName, format, 10);
        if (similarNames.length > 0) {
          const payload = similarNames.map(n => ({ card: n, quantity: 1 }));
          const results = await Api.searchDecklist(payload, domain, gameId);
          const inStock = (results || []).filter(r => r.found > 0);
          const alternatives = inStock.slice(0, 5).map(item => {
            const allVariants = (item.products || []).flatMap(p =>
              (p.variants || []).map(v => ({
                name: `${p.title} ${v.title}`, searchName: item.searchName,
                img: p.img, price: v.price, quantity: v.quantity,
                shopifyId: v.shopifyId, quantityUsed: 1,
              }))
            );
            return allVariants.find(v => v.quantity > 0) || allVariants[0];
          }).filter(Boolean);
          const result = { isBasicLand: false, notFoundMatch: false, alternatives };
          this._cache[cacheKey] = result;
          return result;
        }
      } catch {}
      const result = { isBasicLand: false, notFoundMatch: false, alternatives: [] };
      this._cache[cacheKey] = result;
      return result;
    },
  };

  /* ============================================================
     MONEY FORMAT
  ============================================================ */
  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents * 100, window.dcgDecklist.moneyFormat);
    }
    return '$' + Number(cents).toFixed(2);
  }

  /* ============================================================
     FILTER ENGINE
  ============================================================ */
  function applyFilters(found, filters, conditions, gameConfig) {
    const game = gameConfig || {};
    const allConditions = filters.conditions.length > 0 ? filters.conditions : conditions;
    const allPrintings = filters.printings.length > 0
      ? filters.printings
      : (game.printings || []).map(p => p.tcgname.toLowerCase() === 'normal' ? '' : p.tcgname);
    const acceptedTitles = [];
    allPrintings.forEach(printing => {
      allConditions.forEach(condition => {
        const fragment = printing === '' ? condition : `${condition} ${printing}`;
        acceptedTitles.push(fragment.toLowerCase());
      });
    });

    return found.map(item => {
      const matched = [], fulfilled = [], unmatched = [];
      let qtyFulfilled = 0;
      const requested = item._remainingRequested || item.requestedAmount || item.requested || 1;
      const sorted = [...(item.products || [])].sort((a, b) =>
        (b.variants[0]?.quantity || 0) - (a.variants[0]?.quantity || 0));

      // fulfilled is limited to ONE variant from ONE product per card slot.
      //
      // Why: the deck builder doesn't know real inventory pre-probe (BinderPOS
      // doesn't expose counts through the storefront). Every available variant
      // returns quantity=1 from our conservative fallback. Stacking them would
      // create e.g. 4 separate "Dreepy" cart entries (NM+LP+MP+HP each qty=1)
      // or two "Budew" entries from different printings — both wrong.
      // One entry per slot; the probe in openVerifyModal() sends _requestedAmount
      // and lets Shopify enforce the real ceiling.
      let fulfillmentLocked = false; // true once we have our one fulfilled entry

      sorted.forEach(product => {
        (product.variants || []).forEach(variant => {
          const varTitle = variant.title.toLowerCase();
          const variantObj = {
            searchName: item.searchName, shopifyId: variant.shopifyId,
            name: `${product.title} ${variant.title}`, img: product.img,
            price: variant.price, quantity: variant.quantity, quantityUsed: 0,
          };
          const isMatch = acceptedTitles.length === 0
            ? true : acceptedTitles.some(t => varTitle.includes(t));
          if (isMatch) {
            matched.push(variantObj);
            // Take only the first in-stock matching variant across all products.
            // The probe reveals the real quantity; stacking fake 1s is wrong.
            if (!fulfillmentLocked && variant.quantity > 0) {
              const toUse = Math.min(variant.quantity, requested);
              variantObj.quantityUsed = toUse;
              qtyFulfilled = toUse;
              fulfilled.push({ ...variantObj });
              fulfillmentLocked = true;
            }
          } else {
            unmatched.push(variantObj);
          }
        });
      });

      const altProducts = item.alternativeProducts || [];
      altProducts.forEach(product => {
        (product.variants || []).filter(v => v.quantity > 0).forEach(variant => {
          unmatched.push({
            searchName: item.searchName, shopifyId: variant.shopifyId,
            name: `${product.title} ${variant.title}`, img: product.img,
            price: variant.price, quantity: variant.quantity, quantityUsed: 0,
          });
        });
      });

      return {
        name: item.searchName, displayName: item.displayName,
        setCode: item.setCode, requestedAmount: requested,
        fulfilledAmount: qtyFulfilled, matched, fulfilled, unmatched,
        exactSetMatch: item.exactSetMatch !== false,
        imageUrl: item.imageUrl || fulfilled[0]?.img || '',
      };
    }).sort((a, b) => a.name.localeCompare(b.name));
  }

  /* ============================================================
     MAIN APP CLASS
  ============================================================ */
  class DecklistApp {
    constructor(rootEl, config) {
      this.root = rootEl;
      this.domain = config.permanentDomain;
      this.config = config;

      this.state = {
        stage: 'loading',
        error: null,
        supportedGames: [],
        storeConditions: [],
        selectedGameId: null,
        selectedFormat: null,
        selectedConditions: [],
        selectedPrintings: [],
        decklistText: '',
        rawResults: null,
        filteredFound: [],
        cartItems: [],
        autocompleteResults: [],
        autocompleteOpen: false,
        autocompleteQuery: '',
        showVerifyModal: false,
        verifyList: [],
        verifyAcknowledged: false,
        cartSnapshot: null,   // pre-probe cart state; null = no active probe
        recommendations: {},
        copiedShare: false,
        notifyEmails: {},
        pendingAddAll: null,
      };

      this._onDocClick      = this._onDocClick.bind(this);
      this._clickHandler    = e => this._handleClick(e);
      this._changeHandler   = e => this._handleChange(e);
      this._inputHandler    = e => this._handleInput(e);
      this._mouseoverHandler = e => this._handleMouseover(e);
      this._mouseoutHandler  = e => this._handleMouseout(e);

      document.addEventListener('click', this._onDocClick);
      this.root.addEventListener('click',     this._clickHandler);
      this.root.addEventListener('change',    this._changeHandler);
      this.root.addEventListener('input',     this._inputHandler);
      this.root.addEventListener('mouseover', this._mouseoverHandler);
      this.root.addEventListener('mouseout',  this._mouseoutHandler);

      this._autocompleteTimer = null;
      this.init();
    }

    /* ---- Init ---- */
    async init() {
      const urlState = this._readUrlParams();
      try {
        const [games, conditionsRaw] = await Promise.all([
          Api.fetchGames(this.domain),
          Api.fetchConditions(this.domain),
        ]);
        const conditions = (conditionsRaw || [])
          .sort((a, b) => (b.buyMultiplier || 0) - (a.buyMultiplier || 0))
          .map(c => c.name);
        this.state.supportedGames = games || [];
        this.state.storeConditions = conditions;
        if (this.state.supportedGames.length > 0) {
          const deduped = deduplicateGames(this.state.supportedGames);
          const firstLive = deduped.find(g => g._isLive);
          this.state.selectedGameId = urlState.game || firstLive?.gameId || deduped[0]?.gameId || null;
        }
        if (urlState.conditions) this.state.selectedConditions = urlState.conditions;
        if (urlState.printings)  this.state.selectedPrintings  = urlState.printings;
        if (urlState.decklist)   this.state.decklistText        = urlState.decklist;
        this.state.stage = 'submission';
      } catch (err) {
        console.error('[DCG] Init error:', err);
        this.state.error = 'Could not connect to the card search service. Please refresh and try again.';
        this.state.stage = 'error';
      }
      this.render();
      if (urlState.decklist && this.state.stage === 'submission') {
        setTimeout(() => this.submit(), 100);
      }
    }

    /* ---- URL State ---- */
    _readUrlParams() {
      const params = new URLSearchParams(window.location.search);
      const result = {};
      if (params.get('game'))              result.game       = params.get('game');
      if (params.getAll('condition').length) result.conditions = params.getAll('condition');
      if (params.getAll('printing').length)  result.printings  = params.getAll('printing');
      const cards = params.getAll('cards');
      if (cards.length) result.decklist = cards.join('\n');
      return result;
    }

    /* ---- Game helpers ---- */
    _getGameConfig() {
      const id = this.state.selectedGameId || '';
      const canonicalKey = GAME_ID_MAP[id] || id.toLowerCase();
      if (GAME_CONFIGS[canonicalKey]) return GAME_CONFIGS[canonicalKey];
      return this._detectFormatFromText(this.state.decklistText) || GAME_CONFIGS._default;
    }
    _detectFormatFromText(text) {
      if (!text) return null;
      if (/^(Pokémon|Pokemon|Trainer|Energy):/m.test(text)) return GAME_CONFIGS.pokemon;
      if (/^\(\d+\)\s+\S/m.test(text)) return GAME_CONFIGS.fab;
      return null;
    }
    _getGameData() {
      return this.state.supportedGames.find(g => g.gameId === this.state.selectedGameId) || null;
    }
    _getParser() {
      const cfg = this._getGameConfig();
      return Parsers[cfg.parser] || Parsers.generic;
    }

    /* ---- Submit ---- */
    async submit() {
      const text = this.state.decklistText.trim();
      if (!text) { this._focusTextarea(); return; }
      this.state.stage = 'searching';
      this.state.rawResults = null;
      this.state.filteredFound = [];
      this.render();
      try {
        const parser  = this._getParser();
        const cards   = parser(text);
        if (cards.length === 0) throw new Error('No cards found in your decklist. Please check the format and try again.');
        const gameConfig = this._getGameData();
        const useBinderPos = GAME_SEARCH_CONFIG[this.state.selectedGameId]?.useBinderPos ?? false;
        const results = useBinderPos
          ? await Api.searchDecklist(cards, this.domain, this.state.selectedGameId)
          : await Api.searchDecklistViaShopify(cards, this.state.selectedGameId);
        const found = [], noStock = [], notFound = [];
        results.forEach(item => {
          if (item.found > 0)       found.push(item);
          else if (item.validName)  noStock.push(item);
          else                      notFound.push(item);
        });
        noStock.forEach(item => {
          const src = results.find(r => r.searchName === item.searchName);
          // Merge both sources of alternatives:
          //   noStockAlternatives — in-stock products from the full name search
          //   alternativeProducts — in-stock products with different collector number
          //   (e.g. Ultra Ball from SVI/PAL/ASC when MEG 131 is what was requested)
          const combined = [
            ...(src?.noStockAlternatives   || []),
            ...(src?.alternativeProducts   || []),
          ].filter((p, i, arr) =>
            arr.findIndex(q => q.title === p.title) === i // dedupe by title
          );
          if (combined.length > 0) item._alternatives = combined;
          if (src?.imageUrl) item.imageUrl = item.imageUrl || src.imageUrl;
        });
        this.state.rawResults = { found, noStock, notFound };
        this._refilter();

        // Probe real stock counts for every found card — updates filteredFound
        // badges with genuine Shopify-enforced quantities before rendering.
        await this._probeInventory();

        // For partially-fulfilled cards (found but qty < requested), attach
        // alternative printings so they appear in Recommendations.
        // Source: alternativeProducts = other in-stock printings with different
        // set/collector number (e.g. Iono from TEF when PAL is only 1 in stock).
        this.state.filteredFound.forEach(card => {
          if (card.fulfilledAmount < card.requestedAmount) {
            const src = results.find(r => r.searchName === card.name);
            const matchedTitles = new Set((src?.products || []).map(p => p.title));
            // Alternatives pool: useUnmatched (same name, different collector#),
            // noStockAlternatives, PLUS any other in-stock product from the full
            // name search that wasn't the primary match (e.g. non-PAL Iono printings).
            const alts = [
              ...(src?.alternativeProducts || []),
              ...(src?.noStockAlternatives  || []),
              ...(src?._allSearchProducts   || []).filter(p =>
                !matchedTitles.has(p.title) && p.variants.some(v => v.quantity > 0)
              ),
            ].filter((p, i, arr) => arr.findIndex(q => q.title === p.title) === i);
            if (alts.length > 0) {
              card._alternatives        = alts;
              card.searchName           = card.name;
              card.requested            = card.requestedAmount - card.fulfilledAmount;
              card._remainingRequested  = card.requested;
              card._isPartial           = true;
            }
          }
        });

        // Auto-populate cart from exact matches
        const autoAdd = this.state.filteredFound
          .filter(c => c.exactSetMatch !== false)
          .flatMap(c => c.fulfilled.filter(v => v.quantityUsed > 0).map(v => ({
            ...v, _requestedAmount: c.requestedAmount, _searchName: c.name,
          })));
        this.state.cartItems = JSON.parse(JSON.stringify(autoAdd));
        this.state.stage = 'review';
        this.state.recommendations = {};
      } catch (err) {
        console.error('[DCG] Submit error:', err);
        this.state.error = err.message || 'Search failed. Please try again.';
        this.state.stage = 'submission';
        this._showToast(this.state.error);
      }
      this.render();
      if (this.state.stage === 'review') {
        this._loadRecommendations();
        this._loadAlternativePrintings();  // widen alternatives beyond the initial 10 results
      }
    }

    /* ── Section A: inventory probe ──────────────────────────────────────────
     * Runs immediately after search. Adds every found card at its requested
     * amount, reads back what Shopify actually accepts (real stock ceiling),
     * then restores the cart to exactly how it was before.
     * filteredFound cards get their fulfilledAmount updated with real counts.
     * ─────────────────────────────────────────────────────────────────────── */
    async _probeInventory() {
      const toProbe = (this.state.filteredFound || []).filter(c => c.fulfilled.length > 0);
      if (toProbe.length === 0) return;
      try {
        // Step 1: Snapshot the existing cart.
        // /cart/update.js only touches items already in the cart — it cannot add
        // new ones.  We snapshot first so we can restore exactly afterward.
        const snapRes  = await fetch('/cart.js');
        const snapData = snapRes.ok ? await snapRes.json() : { items: [] };
        const snapshot = {};
        (snapData.items || []).forEach(ci => {
          snapshot[String(ci.variant_id)] = ci.quantity;
        });

        // Step 2: Add items via /cart/add.js — the only endpoint that adds NEW line items.
        // We send each variant at its full requested deck amount so Shopify can enforce
        // its real inventory ceiling.
        await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json; charset=utf-8' },
          body: JSON.stringify({
            items: toProbe.map(card => ({
              id:       Number(card.fulfilled[0].shopifyId),
              quantity: card.requestedAmount,
            })),
          }),
        });

        // Step 3: Read the authoritative cart state after Shopify has enforced inventory.
        const afterRes  = await fetch('/cart.js');
        const afterData = afterRes.ok ? await afterRes.json() : { items: [] };
        const cartQtys  = {};
        (afterData.items || []).forEach(ci => {
          cartQtys[String(ci.variant_id)] = ci.quantity;
        });

        // Step 4: Update filteredFound with real quantities.
        // Subtract any quantity already in the cart (snapshot) so we count only
        // what Shopify accepted for this deck request.
        toProbe.forEach(card => {
          const id      = String(card.fulfilled[0].shopifyId);
          const prior   = snapshot[id] ?? 0;
          const total   = cartQtys[id]  ?? prior;
          const realQty = Math.max(0, total - prior);
          card.fulfilledAmount = realQty;
          card.fulfilled.forEach(f => {
            f.quantity     = realQty;
            f.quantityUsed = Math.min(realQty, card.requestedAmount);
          });
        });

        // Step 5: Restore cart to its exact pre-probe state.
        const restore = {};
        toProbe.forEach(card => {
          const id = String(card.fulfilled[0]?.shopifyId);
          if (id) restore[id] = snapshot[id] ?? 0;
        });
        await Cart.rawUpdate(restore);

      } catch (err) {
        console.warn('[DCG] Inventory probe failed, using conservative quantities:', err);
      }
    }
    _refilter() {
      if (!this.state.rawResults) return;
      const gameData = this._getGameData();
      this.state.filteredFound = applyFilters(
        this.state.rawResults.found,
        { conditions: this.state.selectedConditions, printings: this.state.selectedPrintings },
        this.state.storeConditions,
        gameData
      );
    }

    /* ---- Recommendations loader ---- */
    async _loadRecommendations() {
      const notFound = this.state.rawResults?.notFound || [];
      if (notFound.length === 0) return;
      for (let i = 0; i < notFound.length; i += 3) {
        const batch = notFound.slice(i, i + 3);
        await Promise.all(batch.map(async card => {
          const result = await Recommendations.getForCard(
            card.searchName, this.state.selectedGameId, this.domain,
            this.state.selectedConditions, this.state.storeConditions, this.state.selectedFormat
          );
          this.state.recommendations[card.searchName] = { loading: false, ...result };
          this._patchRecDOM();
        }));
      }
    }

    _patchRecDOM() {
      const col3 = this.root.querySelector('.dcg-col--recs .dcg-col__body');
      if (!col3) return;
      const notFound = this.state.rawResults?.notFound || [];
      notFound.forEach(card => {
        const existing = col3.querySelector(`[data-rec-card="${CSS.escape(card.searchName)}"]`);
        if (existing) {
          existing.innerHTML = this._renderRecCardInner(card);
        }
      });
    }

    /* ---- Load broader alternatives for noStock cards ---- */
    async _loadAlternativePrintings() {
      const noStock = this.state.rawResults?.noStock || [];
      if (noStock.length === 0) return;

      const BATCH = 4;
      for (let i = 0; i < noStock.length; i += BATCH) {
        await Promise.all(noStock.slice(i, i + BATCH).map(item => this._fetchMoreAlternatives(item)));
      }

      // Same snapshot/add/restore probe as Section A — but patches the DOM
      // directly (text only) to avoid triggering _renderRecCardInner's cartId
      // filtering, which would incorrectly show "Added to cart" for variants
      // that appear in both Inventory Results and Recommendations.
    }


    async _fetchMoreAlternatives(item) {
      // Strip set/collector info from searchName to get a clean title
      const cardTitle = (item.displayName || item.searchName)
        .replace(/\s*\([^)]+\)\s*$/, '').trim();

      // Game filter — only keep products matching the currently selected game
      const gameConfig   = GAME_SEARCH_CONFIG[this.state.selectedGameId] || {};
      const expectedType = (gameConfig.productType || '').toLowerCase();

      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(cardTitle)}&type=product&limit=10&resources[type]=product`
        );
        if (!res.ok) return;
        const prods = (await res.json())?.resources?.results?.products || [];

        const newProds = (await Promise.all(prods.map(async p => {
          const handle = p.url?.split('/products/')[1]?.split('?')[0];
          if (!handle) return null;
          try {
            const r = await fetch(`/products/${handle}.js`);
            if (!r.ok) return null;
            const d = await r.json();

            // Must start with the card name to avoid stray matches
            if (!d.title.toLowerCase().startsWith(cardTitle.toLowerCase())) return null;

            // Must match the current game's product type — prevents cross-game
            // contamination (e.g. a Lorcana "Boss's Orders" appearing in Pokémon recs)
            if (expectedType) {
              /* see note at the primary type check: the field is `type`, not `product_type` */
              const pt     = (d.type || d.product_type || '').trim().toLowerCase();
              const ptCore = pt.replace(/s$/, '');
              const etCore = expectedType.replace(/s$/, '');
              if (pt && ptCore !== etCore) return null;
              if (!pt) {
                // No product_type set — fall back to vendor/tag check
                const normalise = s => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                const vendor = normalise(d.vendor || '');
                const GAME_VENDORS = {
                  'one piece single': ['bandai'], 'pokemon single': ['pokemon', 'pokémon'],
                  'flesh and blood single': ['legend story studios', 'lss'],
                  'yugioh single': ['konami'], 'lorcana single': ['ravensburger'],
                  'digimon single': ['bandai'],
                };
                const knownVendors = GAME_VENDORS[expectedType] || [];
                const vendorMatch  = knownVendors.some(v => vendor.includes(normalise(v)));
                const tagsStr      = Array.isArray(d.tags) ? d.tags.join(' ') : (d.tags || '');
                const tagMatch     = normalise(tagsStr).includes(expectedType.split(' ')[0]);
                if (!vendorMatch && !tagMatch) return null;
              }
            }

            const inStockVars = (d.variants || []).filter(v => v.available);
            if (inStockVars.length === 0) return null;

            // Exact match = title is precisely the card name (word boundary after it).
            // Expanded match = card name is just a prefix (e.g. "Iono's Tadbulb" for "Iono").
            const tl      = d.title.toLowerCase();
            const nl      = cardTitle.toLowerCase();
            const suffix  = tl.slice(nl.length);
            const isExact = suffix.length === 0 || /^[^a-z']/i.test(suffix);

            return {
              title: d.title,
              img:   d.featured_image || '',
              _exact: isExact,
              variants: inStockVars.map(v => ({
                shopifyId: String(v.id),
                title:     v.title,
                price:     v.price / 100,
                quantity:  1,  // conservative — real qty enforced at probe
              })),
            };
          } catch { return null; }
        }))).filter(Boolean);

        // Two-pass selection: prefer exact name matches (e.g. "Iono" cards only).
        // Fall back to expanded matches (e.g. "Iono's Tadbulb") only if no exact
        // matches were found — this is the "second pass" Cole requested.
        const exactProds = newProds.filter(p => p._exact);
        const useProds   = exactProds.length > 0 ? exactProds : newProds;

        // Merge, deduplicating by shopifyId
        const seen = new Set(
          (item._alternatives || []).flatMap(p => (p.variants || []).map(v => v.shopifyId))
        );
        let changed = false;
        const merged = [...(item._alternatives || [])];

        useProds.forEach(p => {
          const fresh = p.variants.filter(v => !seen.has(v.shopifyId));
          if (fresh.length > 0) {
            merged.push({ ...p, variants: fresh });
            fresh.forEach(v => seen.add(v.shopifyId));
            changed = true;
          }
        });

        if (!changed) return;
        item._alternatives = merged;

        // If this card has no alts yet it was in withoutAlts — move it to withAlts
        // by patching the rawResults so the next render picks it up.
        // Also patch the DOM immediately.
        const el = this.root?.querySelector(`[data-rec-card="${CSS.escape(item.searchName)}"]`);
        if (el) {
          el.innerHTML = this._renderRecCardInner(item);
        } else {
          // Card wasn't rendered yet (was in withoutAlts section) — full re-render
          this.render();
        }
      } catch { /* ignore — alternatives are best-effort */ }
    }

    /* ---- Cart operations ---- */
    _addToCart(variant, opts = {}) {
      const { requestedAmount = variant.quantityUsed || 1, searchName = '' } = opts;
      const existing = this.state.cartItems.find(i => i.shopifyId === variant.shopifyId);
      const maxQty = Math.min(variant.quantity, requestedAmount);
      const toUse  = Math.max(1, Math.min(maxQty, requestedAmount));
      if (existing) {
        existing.quantityUsed = Math.min(existing.quantity, existing.quantityUsed + toUse);
      } else {
        this.state.cartItems.push({
          shopifyId: variant.shopifyId,
          name:      variant.name,
          displayName: variant.name.split(' Near ')[0].split(' Light ')[0].split(' Moderately ')[0].trim(),
          img:       variant.img || '',
          price:     variant.price,
          quantity:  variant.quantity,
          quantityUsed: toUse,
          _requestedAmount: requestedAmount,
          _searchName: searchName,
          setCode: variant.setCode || '',
        });
      }
      this.state.cartItems.sort((a, b) => a.name.localeCompare(b.name));
    }

    _addAllFound(force = false) {
      const toAdd = this.state.filteredFound
        .filter(c => c.exactSetMatch !== false && c.fulfilled.length > 0);
      // Check for duplicates
      if (!force) {
        const dupes = toAdd.filter(c =>
          c.fulfilled.some(v => this.state.cartItems.find(i => i.shopifyId === v.shopifyId))
        );
        if (dupes.length > 0) {
          this.state.pendingAddAll = toAdd;
          this.render();
          return;
        }
      }
      this.state.pendingAddAll = null;
      toAdd.forEach(card => {
        card.fulfilled.filter(v => v.quantityUsed > 0).forEach(v => {
          this._addToCart(v, { requestedAmount: card.requestedAmount, searchName: card.name });
        });
      });
      this.render();
    }

    _addAllAlts() {
      const noStock = this.state.rawResults?.noStock || [];
      noStock.forEach(item => {
        const alts = item._alternatives || [];
        if (alts.length === 0) return;
        const best = alts[0];
        const inStockVariants = (best.variants || []).filter(v => v.quantity > 0);
        if (!inStockVariants.length) return;
        const bestVariant = inStockVariants[0];
        const total = inStockVariants.reduce((s, v) => s + v.quantity, 0);
        this._addToCart({
          shopifyId: bestVariant.shopifyId,
          name: `${best.title} ${bestVariant.title}`,
          img: best.img || '',
          price: bestVariant.price,
          quantity: total,
          quantityUsed: Math.min(total, item.requested || 1),
        }, { requestedAmount: item.requested || 1, searchName: item.searchName });
      });
      this.render();
    }

    _removeFromCart(shopifyId) {
      this.state.cartItems = this.state.cartItems.filter(i => i.shopifyId !== shopifyId);
      this.render();
    }

    _adjustCartQty(shopifyId, delta) {
      const item = this.state.cartItems.find(i => i.shopifyId === shopifyId);
      if (!item) return;
      // Cap at whichever is lower: real stock (item.quantity, updated by probe)
      // OR the player's requested amount.  Pre-probe item.quantity=1 (conservative
      // fallback), post-probe it is the Shopify-enforced real count.
      const maxQty = Math.min(item.quantity, item._requestedAmount || item.quantity);
      const newQty = item.quantityUsed + delta;
      if (newQty < 1) { this._removeFromCart(shopifyId); return; }
      item.quantityUsed = Math.min(maxQty, newQty);
      this.render();
    }

    _setCartQty(shopifyId, val) {
      const item = this.state.cartItems.find(i => i.shopifyId === shopifyId);
      if (!item) return;
      const parsed = parseInt(val, 10);
      if (isNaN(parsed) || parsed < 1) { this._removeFromCart(shopifyId); return; }
      const maxQty = Math.min(item.quantity, item._requestedAmount || item.quantity);
      item.quantityUsed = Math.min(maxQty, parsed);
      const inputEl = this.root.querySelector(`[data-cart-qty-input="${shopifyId}"]`);
      if (inputEl) inputEl.value = item.quantityUsed;
    }

    _cartTotal() {
      return this.state.cartItems.reduce((s, i) => s + i.price * i.quantityUsed, 0);
    }

    /* ---- Progress ---- */
    _calcProgress() {
      const raw = this.state.rawResults;
      if (!raw) return { pct: 0, cartCards: 0, totalCards: 0 };
      const allResults = [...(raw.found || []), ...(raw.noStock || []), ...(raw.notFound || [])];
      const totalCards = allResults.reduce((s, r) => s + (r.requested || r.quantity || 1), 0);
      const cartCards  = this.state.cartItems.reduce((s, i) => s + i.quantityUsed, 0);
      const pct = totalCards > 0 ? Math.min(100, Math.round((cartCards / totalCards) * 100)) : 0;
      return { pct, cartCards, totalCards };
    }

    /* ---- Autocomplete ---- */
    _scheduleAutocomplete(query) {
      clearTimeout(this._autocompleteTimer);
      this.state.autocompleteQuery = query;
      if (!query || query.length < 2) {
        this.state.autocompleteResults = [];
        this.state.autocompleteOpen    = false;
        this._patchAutocomplete();
        return;
      }
      this._autocompleteTimer = setTimeout(() => this._fetchAutocomplete(query), 320);
    }

    async _fetchAutocomplete(query) {
      try {
        const gameConfig   = GAME_SEARCH_CONFIG[this.state.selectedGameId] || {};
        const expectedType = (gameConfig.productType || '').toLowerCase();
        const url = `/search/suggest.json?q=${encodeURIComponent(query)}&type=product&limit=8&resources[type]=product`;
        const res  = await fetch(url);
        if (!res.ok) return;
        const data = await res.json();
        let products = data?.resources?.results?.products || [];
        if (expectedType) {
          products = products.filter(p => {
            const handle = p.url?.split('/products/')[1]?.split('?')[0];
            return handle; // filter by type happens after product.js fetch — just show suggestions for now
          });
        }
        if (this.state.autocompleteQuery !== query) return; // stale
        this.state.autocompleteResults = products.slice(0, 6);
        this.state.autocompleteOpen    = products.length > 0;
        this._patchAutocomplete();
      } catch { /* silent */ }
    }

    _patchAutocomplete() {
      const dd = this.root.querySelector('.dcg-autocomplete__dropdown');
      if (!dd) return;
      if (!this.state.autocompleteOpen || this.state.autocompleteResults.length === 0) {
        dd.hidden = true;
        return;
      }
      dd.hidden = false;
      dd.innerHTML = this.state.autocompleteResults.map(p => {
        const title  = this._escape(p.title || '');
        const price  = p.price ? `$${(p.price / 100).toFixed(2)}` : '';
        const handle = (p.url?.split('/products/')[1]?.split('?')[0] || '');
        return `<div class="dcg-autocomplete__item" data-action="autocomplete-select" data-handle="${this._escape(handle)}" data-title="${title}" data-price="${this._escape(price)}">
          <span class="dcg-autocomplete__name">${title}</span>
          <span class="dcg-autocomplete__price">${price}</span>
        </div>`;
      }).join('');
    }

    async _selectAutocomplete(handle, title, priceStr) {
      this.state.autocompleteOpen = false;
      this.state.autocompleteResults = [];
      const input = this.root.querySelector('.dcg-autocomplete__input');
      if (input) input.value = '';
      this._patchAutocomplete();
      if (!handle) { this._showToast('Could not load card details.'); return; }
      try {
        const res  = await fetch(`/products/${handle}.js`);
        if (!res.ok) throw new Error();
        const data = await res.json();
        const variants = (data.variants || []).filter(v => v.available);
        if (variants.length === 0) { this._showToast('No stock available for that card.'); return; }
        const best = variants[0];
        this._addToCart({
          shopifyId: String(best.id),
          name: `${data.title} ${best.title}`,
          img:  data.featured_image || '',
          price: (best.price || 0) / 100,
          quantity: best.inventory_quantity > 0 ? best.inventory_quantity : 1,
          quantityUsed: 1,
        }, { requestedAmount: 1, searchName: data.title });
        this.render();
        this._showToast(`${data.title} added to cart`);
      } catch { this._showToast('Could not load card details.'); }
    }

    /* ---- Wishlist ---- */
    _getWishlist()           { try { return JSON.parse(localStorage.getItem('dcg_wishlist') || '[]'); } catch { return []; } }
    _isWishlisted(name)      { return this._getWishlist().some(i => i.name === name); }
    _toggleWishlist(name, img) {
      const list = this._getWishlist();
      const idx  = list.findIndex(i => i.name === name);
      if (idx > -1) { list.splice(idx, 1); } else { list.push({ name, img, addedAt: new Date().toISOString() }); }
      try { localStorage.setItem('dcg_wishlist', JSON.stringify(list)); } catch (_) {}
      return idx === -1;
    }

    /* ---- Notify me ---- */
    _getNotifyStore()    { try { return JSON.parse(localStorage.getItem('dcg_notify') || '{}'); } catch { return {}; } }
    _saveNotify(name, email) {
      const store = this._getNotifyStore();
      store[name] = { email, savedAt: new Date().toISOString() };
      try { localStorage.setItem('dcg_notify', JSON.stringify(store)); } catch (_) {}
    }
    _isNotified(name)    { return !!this._getNotifyStore()[name]; }

    /* ---- Verify modal ---- */
    async openVerifyModal() {
      if (this.state.cartItems.length === 0) return;

      // ── Cart probe ────────────────────────────────────────────────────────
      // 1. Snapshot the current cart so we can restore it if the user cancels.
      // 2. Call Cart.update() — Shopify enforces real inventory and returns
      //    the actual quantities it accepted. We use those numbers in the modal
      //    so the player sees truthful stock counts before confirming.
      try {
        // Step 1: snapshot
        const snapRes  = await fetch('/cart.js');
        const snapData = snapRes.ok ? await snapRes.json() : { items: [] };
        const snapshot = {};
        (snapData.items || []).forEach(ci => {
          snapshot[String(ci.variant_id)] = ci.quantity;
        });
        this.state.cartSnapshot = snapshot;

        // Step 2: commit Section A quantities to the Shopify cart.
        // Section A (_probeInventory) already ran /cart/add.js at requestedAmount
        // and enforced real inventory — item.quantityUsed already reflects what
        // Shopify will actually allow.  Sending _requestedAmount here would let
        // products with inventory_policy:continue bypass that enforcement and
        // put the full deck quantity (e.g. 4 Dreepy) into the cart even when
        // only 1 is in stock.  We use quantityUsed so the verify modal
        // commits exactly what Section A validated.
        const probeData = await Cart.update(this.state.cartItems);

        // Step 3: reconcile quantities
        if (probeData && Array.isArray(probeData.items)) {
          this.state.cartItems.forEach(item => {
            const cartItem = probeData.items.find(
              ci => String(ci.variant_id) === String(item.shopifyId)
            );
            const realQty = cartItem ? cartItem.quantity : 0;
            item.quantity     = realQty;
            item.quantityUsed = realQty;
          });
        }
      } catch (err) {
        console.warn('[DCG] Cart probe failed, proceeding with estimated quantities:', err);
        // snapshot stays null — closeVerifyModal will skip restore on cancel
      }
      // ─────────────────────────────────────────────────────────────────────

      this.state.cartItems = this.state.cartItems.filter(i => i.quantityUsed > 0);

      if (this.state.cartItems.length === 0) {
        this._showToast('None of those cards are currently in stock.');
        // Restore cart snapshot on the way out (probe may have zeroed things)
        if (this.state.cartSnapshot) {
          Cart.rawUpdate(this.state.cartSnapshot).catch(() => {});
          this.state.cartSnapshot = null;
        }
        return;
      }

      this.state.verifyList         = JSON.parse(JSON.stringify(this.state.cartItems)).map(i => ({ ...i, checked: true }));
      this.state.showVerifyModal    = true;
      this.state.verifyAcknowledged = false;
      this.render();
      this._renderVerifyModal();
    }

    closeVerifyModal() {
      this.state.showVerifyModal = false;
      const el = document.getElementById('dcg-verify-modal');
      if (el) el.remove();

      // If cartSnapshot is still set the user cancelled — restore the cart to
      // its pre-probe state.  confirmVerifyModal() clears cartSnapshot before
      // calling closeVerifyModal() so this branch is skipped on confirm.
      if (this.state.cartSnapshot) {
        const restoreUpdates = {};
        (this.state.verifyList || []).forEach(item => {
          restoreUpdates[item.shopifyId] = this.state.cartSnapshot[String(item.shopifyId)] ?? 0;
        });
        Cart.rawUpdate(restoreUpdates).catch(() => {});
        this.state.cartSnapshot = null;
      }
    }

    async confirmVerifyModal() {
      const checked   = this.state.verifyList.filter(i => i.checked);
      const unchecked = this.state.verifyList.filter(i => !i.checked);
      if (checked.length === 0) { this.closeVerifyModal(); return; }

      // The probe in openVerifyModal() already wrote the correct quantities for
      // every item into the Shopify cart.  All we need to do now is:
      //   • Remove (or restore to snapshot) anything the user unchecked.
      //   • Update the cart dropdown, then redirect.
      //
      // Clear cartSnapshot BEFORE calling closeVerifyModal so the cancel-restore
      // branch is skipped — the user confirmed, so we keep the cart as-is.
      const snapshot = this.state.cartSnapshot;
      this.state.cartSnapshot = null;

      try {
        if (unchecked.length > 0) {
          const restoreUpdates = {};
          unchecked.forEach(item => {
            // Restore to pre-probe quantity, or 0 if it wasn't in the cart before.
            restoreUpdates[item.shopifyId] = snapshot
              ? (snapshot[String(item.shopifyId)] ?? 0)
              : 0;
          });
          await Cart.rawUpdate(restoreUpdates);
        }

        if (window.Shopify?.adjustCartDropDown) window.Shopify.adjustCartDropDown();
        this.closeVerifyModal();
        window.location.href = '/cart';
      } catch (err) {
        console.error('[DCG] Cart confirm error:', err);
        this._showToast('Could not update cart. Please try again.');
        // Re-arm snapshot so cancel restore still works if modal stays open
        this.state.cartSnapshot = snapshot;
      }
    }


    _renderVerifyModal() {
      const existing = document.getElementById('dcg-verify-modal');
      if (existing) existing.remove();
      if (!this.state.showVerifyModal) return;

      const items       = this.state.verifyList;
      const checked     = items.filter(i => i.checked);
      const total       = checked.reduce((s, i) => s + i.price * i.quantityUsed, 0);
      const allChecked  = items.every(i => i.checked);
      // True if any item was capped below the player's deck request by the probe
      const hasAdjusted = items.some(i => i.quantityUsed < (i._requestedAmount || i.quantityUsed));

      const modal = document.createElement('div');
      modal.id        = 'dcg-verify-modal';
      modal.className = 'dcg-modal-backdrop';
      modal.innerHTML = `
        <div class="dcg-modal" role="dialog" aria-modal="true">
          <div class="dcg-modal__header">
            <h2 class="dcg-modal__title">Verify your order</h2>
            <button class="dcg-modal__close" data-action="close-verify" aria-label="Close">&#x2715;</button>
          </div>
          <p class="dcg-modal__subtitle">${hasAdjusted
            ? '⚠️ Stock adjusted — some quantities are lower than requested. Review before confirming.'
            : 'Quantities reflect actual stock on hand. Uncheck anything you don\'t want.'
          }</p>
          <div class="dcg-modal__list">
            ${items.map((item, idx) => `
              <label class="dcg-modal__item ${item.checked ? '' : 'is-unchecked'}" data-idx="${idx}">
                <input type="checkbox" data-action="verify-toggle" data-idx="${idx}" ${item.checked ? 'checked' : ''}>
                <img class="dcg-modal__item-img" src="${this._escape(item.img || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png')}" alt="${this._escape(item.name)}" loading="lazy">
                <div class="dcg-modal__item-info">
                  <span class="dcg-modal__item-name">${this._escape(item.name)}</span>
                  ${(function(q, r) {
                    return q < r
                      ? '<span class="dcg-modal__item-qty dcg-modal__item-qty--reduced">&times;' + q +
                        ' <span class="dcg-modal__item-qty-note">(wanted &times;' + r + ')</span></span>'
                      : '<span class="dcg-modal__item-qty">&times;' + q + '</span>';
                  })(item.quantityUsed, item._requestedAmount || item.quantityUsed)}
                </div>
                <span class="dcg-modal__item-price">${formatMoney(item.price * item.quantityUsed)}</span>
              </label>
            `).join('')}
          </div>
          <div class="dcg-modal__ack">
            <label>
              <input type="checkbox" data-action="verify-ack" id="dcg-ack-check">
              <span>I have reviewed this order and confirm the cards I want to purchase.</span>
            </label>
          </div>
          <div class="dcg-modal__footer">
            <label class="dcg-modal__select-all">
              <input type="checkbox" data-action="verify-all" ${allChecked ? 'checked' : ''}> Select all
            </label>
            <div class="dcg-modal__footer-right">
              <span class="dcg-modal__total">Total: <strong>${formatMoney(total)}</strong> <span class="dcg-modal__count">(${checked.length} of ${items.length})</span></span>
              <button class="dcg-btn dcg-btn--ghost" data-action="close-verify">Cancel</button>
              <button class="dcg-btn dcg-btn--primary" data-action="confirm-verify" id="dcg-confirm-btn"
                ${checked.length === 0 || !this.state.verifyAcknowledged ? 'disabled' : ''}>
                Add ${checked.length} card${checked.length !== 1 ? 's' : ''} to Cart
              </button>
            </div>
          </div>
        </div>`;

      document.body.appendChild(modal);

      modal.addEventListener('click', e => {
        const action = e.target.closest('[data-action]')?.dataset.action;
        if (!action) return;
        if (action === 'close-verify')   { this.closeVerifyModal(); return; }
        if (action === 'confirm-verify') { this.confirmVerifyModal(); return; }
        if (action === 'verify-toggle') {
          const idx = parseInt(e.target.dataset.idx, 10);
          this.state.verifyList[idx].checked = e.target.checked;
          this._renderVerifyModal();
        }
        if (action === 'verify-all') {
          this.state.verifyList.forEach(i => i.checked = e.target.checked);
          this._renderVerifyModal();
        }
        if (action === 'verify-ack') {
          this.state.verifyAcknowledged = e.target.checked;
          const btn     = document.getElementById('dcg-confirm-btn');
          const hasItems = this.state.verifyList.some(i => i.checked);
          if (btn) btn.disabled = !e.target.checked || !hasItems;
        }
      });

      modal.addEventListener('click', e => { if (e.target === modal) this.closeVerifyModal(); });
      const onKey = e => { if (e.key === 'Escape') { this.closeVerifyModal(); document.removeEventListener('keydown', onKey); } };
      document.addEventListener('keydown', onKey);
    }

    /* ============================================================
       RENDER
    ============================================================ */
    render() {
      const { stage } = this.state;
      if (stage === 'loading') {
        this.root.innerHTML = this._renderLoading();
      } else if (stage === 'error') {
        this.root.innerHTML = this._renderError();
      } else {
        this.root.innerHTML = this._renderApp();
      }
      if (!document.getElementById('dcg-card-preview')) {
        const img = document.createElement('img');
        img.id = 'dcg-card-preview';
        img.className = 'dcg-card-preview-img';
        document.body.appendChild(img);
      }
      if (this.state.showVerifyModal) this._renderVerifyModal();
    }

    _renderLoading() {
      return `<div class="dcg-dl"><div class="dcg-loading"><div class="dcg-spinner"></div><p>Loading deck builder&hellip;</p></div></div>`;
    }

    _renderError() {
      return `<div class="dcg-dl"><div class="dcg-error"><h2>Something went wrong</h2><p>${this._escape(this.state.error)}</p><button class="dcg-btn dcg-btn--ghost" onclick="location.reload()">Refresh</button></div></div>`;
    }

    /* ---- Main App ---- */
    _renderApp() {
      const { stage } = this.state;
      const progress  = this._calcProgress();
      const isReview  = stage === 'review';
      const isSearching = stage === 'searching';

      return `
        <div class="dcg-dl">
          ${this._renderTopBar(progress)}
          <div class="dcg-dl__body">
            ${this._renderColumns(isReview, isSearching)}
            ${this._renderCartSection()}
          </div>
        </div>
        ${this.state.pendingAddAll ? this._renderAddAllWarning() : ''}
      `;
    }

    /* ---- Top Bar ---- */
    _renderTopBar(progress) {
      const { supportedGames, storeConditions, selectedGameId, selectedFormat,
              selectedConditions, selectedPrintings } = this.state;
      const gameCfg = this._getGameConfig();
      const gameData = this._getGameData();

      const printingOptions = (gameData?.printings || []).map(p => ({
        label: p.tcgname.charAt(0).toUpperCase() + p.tcgname.slice(1),
        value: p.tcgname.toLowerCase() === 'normal' ? '' : p.tcgname,
      }));

      /* ── Game dropdown filter — DCG 03 AUG 2026 (F-DECK-4) ──────────────
         Was: ['mtg', 'pokemon', 'fab', 'onepiece', 'one'] filtered against
         g.gameId. That list mixed two different namespaces:

           raw gameId   (from BinderPOS)  mtg, pokemon, fleshAndBlood, one, dgm…
           canonical    (via GAME_ID_MAP) mtg, pokemon, fab,           onepiece…

         `fab` and `onepiece` are CANONICAL keys, but the filter compares
         against `g.gameId`, which is always RAW. So neither could ever match:
         'fab' never equals 'fleshAndBlood', and 'onepiece' never equals 'one'.
         'one' happened to be in the list too, which is why One Piece survived
         and Flesh and Blood silently vanished from the dropdown.

         Verified live 03 AUG 2026: the select offered only Magic, Pokémon and
         One Piece, and BinderPOS's own gameId list is dgm, fleshAndBlood, fow,
         gar, lor, mtg, one, pokemon, swu, yugioh.

         Fixed by deleting the duplicate list and reusing LIVE_GAME_IDS, which
         already holds exactly the intended set in the RAW namespace and is
         what isGameLive() and the liveOrder sort both use. One source of
         truth, so the two lists can no longer drift apart.

         Safe to enable: GAME_SEARCH_CONFIG.fleshAndBlood exists (Shopify path,
         productType 'Flesh And Blood Single'), GAME_CONFIGS.fab supplies the
         label and parser, and the store stocks F&B singles with that exact
         product_type. Validate in the preview theme before publishing — this
         path has not run in production. */
      const deduped = deduplicateGames(supportedGames).filter(g => LIVE_GAME_IDS.has(g.gameId));
      const pct     = progress.pct;
      const hasResults = !!this.state.rawResults;

      return `
        <div class="dcg-topbar">
          <div class="dcg-topbar__row">
            ${deduped.length > 1 ? `
              <div class="dcg-topbar__group">
                <label class="dcg-topbar__label">Game</label>
                <div class="dcg-select-wrap">
                  <select class="dcg-select" data-action="select-game">
                    ${deduped.map(g => `
                      <option value="${this._escape(g.gameId)}"
                        ${g.gameId === selectedGameId ? 'selected' : ''}
                        ${!g._isLive ? 'disabled' : ''}
                      >${this._escape(g._displayName)}${!g._isLive ? ' — Coming Soon' : ''}</option>
                    `).join('')}
                  </select>
                </div>
              </div>
            ` : ''}

            ${gameCfg.formats.length > 0 ? `
              <div class="dcg-topbar__group">
                <label class="dcg-topbar__label">Format</label>
                <div class="dcg-select-wrap">
                  <select class="dcg-select" data-action="select-format">
                    <option value="">All Formats</option>
                    ${gameCfg.formats.map(f => `<option value="${this._escape(f)}" ${f === selectedFormat ? 'selected' : ''}>${this._escape(f)}</option>`).join('')}
                  </select>
                </div>
              </div>
            ` : ''}

            <div class="dcg-topbar__group">
              <label class="dcg-topbar__label">Condition</label>
              ${this._renderMultiSelect('conditions', storeConditions.map(c => ({ label: c, value: c })), selectedConditions, 'Any condition')}
            </div>

            ${printingOptions.length > 0 ? `
              <div class="dcg-topbar__group">
                <label class="dcg-topbar__label">Printing</label>
                ${this._renderMultiSelect('printings', printingOptions, selectedPrintings, 'Any printing')}
              </div>
            ` : ''}

            <div class="dcg-topbar__spacer"></div>

            ${hasResults ? `
              <div class="dcg-topbar__stats">
                <!-- DCG 03 AUG 2026: replaced Found/Missing/Cart, which counted
                     DISTINCT entries and so read "20" for a 60-card deck. This
                     reports actual card quantity covered, carrying over the
                     figure from the progress bar that was removed below. -->
                <span class="dcg-topbar__stat"><span class="dcg-topbar__stat-label">Deck covered</span><strong class="c-cart">${progress.cartCards} / ${progress.totalCards}</strong></span>
              </div>
            ` : ''}
          </div>

        </div>
      `;
    }

    _renderMultiSelect(name, options, selected, placeholder) {
      const count = selected.length;
      const label = count === 0 ? placeholder : `${count} selected`;
      return `
        <div class="dcg-multiselect" data-multiselect="${name}">
          <button class="dcg-multiselect__trigger" type="button" data-action="toggle-multiselect" data-target="${name}">${this._escape(label)}</button>
          <div class="dcg-multiselect__dropdown" data-dropdown="${name}">
            ${options.map(opt => `
              <label class="dcg-multiselect__option">
                <input type="checkbox" data-multiselect-item="${name}" value="${this._escape(opt.value)}" ${selected.includes(opt.value) ? 'checked' : ''}>
                ${this._escape(opt.label)}
              </label>
            `).join('')}
          </div>
        </div>`;
    }

    /* ---- Three columns ---- */
    _renderColumns(isReview, isSearching) {
      return `
        <div class="dcg-columns">
          ${this._renderCol1()}
          ${this._renderCol2(isReview, isSearching)}
          ${this._renderCol3(isReview)}
        </div>`;
    }

    /* ---- Column 1: Decklist ---- */
    _renderCol1() {
      const cfg = this._getGameConfig();
      return `
        <div class="dcg-col dcg-col--list">
          <div class="dcg-col__hd">
            <div class="dcg-col__hd-left">
              <span class="dcg-col__num">①</span>
              <span class="dcg-col__title">Your Decklist</span>
            </div>
          </div>
          <div class="dcg-col__body">
            <div class="dcg-col1-inner">
              <textarea class="dcg-textarea" data-action="decklist-input" spellcheck="false" autocomplete="off"
                placeholder="${this._escape(cfg.placeholder)}">${this._escape(this.state.decklistText)}</textarea>
              <button class="dcg-btn dcg-btn--primary dcg-btn--full" data-action="submit">
                ${this.state.stage === 'searching' ? '&hellip; Searching' : 'Search Inventory →'}
              </button>
            </div>
          </div>
        </div>`;
    }

    /* ---- Column 2: Inventory ---- */
    _renderCol2(isReview, isSearching) {
      if (isSearching) {
        return `
          <div class="dcg-col dcg-col--results">
            <div class="dcg-col__hd">
              <div class="dcg-col__hd-left"><span class="dcg-col__num">②</span><span class="dcg-col__title">Inventory Results</span></div>
            </div>
            <div class="dcg-col__body dcg-col__body--center">
              <div class="dcg-card-fan">${[0,1,2,3,4].map(i => `<div class="dcg-card-fan__item dcg-card-fan__item--${i}"></div>`).join('')}</div>
              <p class="dcg-searching-text">Searching inventory&hellip;</p>
            </div>
          </div>`;
      }

      if (!isReview) {
        return `
          <div class="dcg-col dcg-col--results">
            <div class="dcg-col__hd">
              <div class="dcg-col__hd-left"><span class="dcg-col__num">②</span><span class="dcg-col__title">Inventory Results</span></div>
            </div>
            <div class="dcg-col__body dcg-col__body--center">
              <p class="dcg-placeholder-text">Paste your decklist and hit<br><strong>Search Inventory</strong> to see results here.</p>
            </div>
          </div>`;
      }

      const found   = this.state.filteredFound.filter(c => c.exactSetMatch !== false);
      const altPrint = this.state.filteredFound.filter(c => c.exactSetMatch === false);
      const noStock = this.state.rawResults?.noStock || [];
      const notFound = this.state.rawResults?.notFound || [];

      const foundCount   = found.length;
      const missingCount = noStock.length + notFound.length;

      /* DCG 03 AUG 2026 — count CARDS, not entries.
         The badge previously showed found.length, i.e. distinct decklist
         lines, so a 60-card deck with 20 unique cards read "20 in stock".
         Players think in card quantities, so report fulfilled-vs-requested
         quantity across every result, then alternatives and missing. */
      const qtyOf  = c => (c.requestedAmount ?? c.requested ?? c.quantity ?? 1);
      const allRes = [...found, ...altPrint, ...noStock, ...notFound];
      const totalQty   = allRes.reduce((s, c) => s + qtyOf(c), 0);
      const inStockQty = [...found, ...altPrint]
        .reduce((s, c) => s + (c.fulfilledAmount ?? 0), 0);
      const altCount   = altPrint.length;

      return `
        <div class="dcg-col dcg-col--results">
          <div class="dcg-col__hd">
            <div class="dcg-col__hd-left"><span class="dcg-col__num">②</span><span class="dcg-col__title">Inventory Results</span></div>
            <span class="dcg-badge-group">
              <span class="dcg-badge dcg-badge--ok">${inStockQty}/${totalQty} in stock</span>
              ${altCount > 0 ? `<span class="dcg-badge dcg-badge--alt">${altCount} alternative${altCount !== 1 ? 's' : ''}</span>` : ''}
              ${missingCount > 0 ? `<span class="dcg-badge dcg-badge--miss">${missingCount} missing</span>` : ''}
            </span>
          </div>
          <div class="dcg-col__body">

            ${found.length > 0 || altPrint.length > 0 ? `
              <div class="dcg-sec-label dcg-sec-label--found">In Stock</div>
              <div class="dcg-sec-action-row">
                <span class="dcg-sec-action-info"><strong>${found.length}</strong> card${found.length !== 1 ? 's' : ''} matched your list</span>
                <button class="dcg-sec-add-btn" data-action="add-all-found">Add all to cart</button>
              </div>
              ${found.map(card => this._renderInventoryCard(card, 'found')).join('')}
              ${altPrint.length > 0 ? `
                <div class="dcg-sec-label dcg-sec-label--alt">Alternative Printings Available</div>
                ${altPrint.map(card => this._renderInventoryCard(card, 'alt')).join('')}
              ` : ''}
            ` : ''}

            ${(noStock.length > 0 || notFound.length > 0) ? `
              <div class="dcg-sec-label dcg-sec-label--missing">Out of Stock</div>
              ${[...noStock, ...notFound].map(item => this._renderOutOfStockRow(item)).join('')}
            ` : ''}

          </div>
        </div>`;
    }

    _renderInventoryCard(card, type) {
      const img = card.imageUrl || (card.fulfilled[0]?.img) || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png';
      const inCart = this.state.cartItems.some(i => i._searchName === card.name);

      /* DCG 03 AUG 2026 — show REAL availability, not the fulfilled count.
         displayQty was card.fulfilledAmount, i.e. how many the probe could
         satisfy, capped at what the decklist asked for. A card with 15 in
         stock requested as a 2-of therefore read "2 in stock", hiding the
         other 13 from a customer who might want them.

         card.fulfilled[0].quantity is now the exact Shopify count, supplied by
         templates/search.inventory.liquid. Note fulfilled holds ONE variant per
         card slot (see the comment in the matcher) — a constraint that existed
         only because BinderPOS reported 1/0, so summing is unnecessary here. */
      const availQty   = card.fulfilled?.[0]?.quantity || 0;
      const requestedN = card.requestedAmount || 1;
      const chosenQty  = Math.max(0, Math.min(
        card._chosenQty !== undefined ? card._chosenQty : requestedN,
        availQty
      ));
      const shortfall  = Math.max(0, requestedN - chosenQty);

      const stockBadge = availQty === 0
        ? `<span class="dcg-stock-badge dcg-stock-badge--none">Out of stock</span>`
        : shortfall > 0
          ? `<span class="dcg-stock-badge dcg-stock-badge--partial">${availQty} in stock &mdash; ${shortfall} short of ${requestedN}</span>` +
            `<span class="dcg-inv-card__see-recs">See Recommendations →</span>`
          : `<span class="dcg-stock-badge dcg-stock-badge--ok">${availQty} in stock</span>`;

      const bestVariant = card.fulfilled[0];
      const price = bestVariant ? formatMoney(bestVariant.price) : '—';
      const addLabel = inCart ? '✓ In cart' : `+ Add ×${chosenQty}`;

      /* Quantity stepper. Capped at real stock only — verified 03 AUG 2026 that
         no single carries a limit-* tag (they exist solely on sealed product and
         event tickets), so window.DCG.parseLimit never applies on this surface.
         The cap is still read through the same min(stock, limit) shape as the
         cart stepper so a future limit tag on a single would be honoured. */
      const limitCap = (window.DCG && typeof window.DCG.parseLimit === 'function' && bestVariant?.tags)
        ? (window.DCG.parseLimit(bestVariant.tags) || Infinity)
        : Infinity;
      const maxQty = Math.max(1, Math.min(availQty, limitCap));
      const stepper = (availQty === 0 || inCart) ? '' : `
            <div class="dcg-inv-qty" data-card-name="${this._escape(card.name)}">
              <button class="dcg-inv-qty__btn" data-action="inv-qty-minus"
                data-card-name="${this._escape(card.name)}"
                ${chosenQty <= 1 ? 'disabled' : ''} aria-label="Decrease quantity">&minus;</button>
              <input class="dcg-inv-qty__input" type="number" inputmode="numeric"
                value="${chosenQty}" min="1" max="${maxQty}"
                data-action="inv-qty-input" data-card-name="${this._escape(card.name)}"
                aria-label="Quantity">
              <button class="dcg-inv-qty__btn" data-action="inv-qty-plus"
                data-card-name="${this._escape(card.name)}"
                ${chosenQty >= maxQty ? 'disabled' : ''} aria-label="Increase quantity">+</button>
            </div>`;

      return `
        <div class="dcg-inv-card ${type === 'alt' ? 'dcg-inv-card--alt' : ''}" data-card-name="${this._escape(card.name)}">
          <img class="dcg-inv-card__img" src="${this._escape(img)}" alt="${this._escape(card.displayName || card.name)}"
            loading="lazy" data-action="preview-card" data-src="${this._escape(img)}">
          <div class="dcg-inv-card__info">
            <div class="dcg-inv-card__name">
              ${this._escape(card.displayName || card.name)}
              ${card.setCode ? `<span class="dcg-set-badge">${this._escape(card.setCode)}</span>` : ''}
              ${type === 'alt' ? `<span class="dcg-alt-badge">Alt print</span>` : ''}
            </div>
            <div class="dcg-inv-card__meta">
              ${card.fulfilled[0] ? this._escape(card.fulfilled[0].name.split(card.displayName || '').pop()?.trim() || '') : ''}
            </div>
            <div class="dcg-inv-card__badges">${stockBadge}</div>
          </div>
          <div class="dcg-inv-card__right">
            <span class="dcg-inv-card__price">${price}</span>
            ${stepper}
            <button class="dcg-inv-card__add ${inCart ? 'is-added' : ''}"
              data-action="add-card-to-cart"
              data-card-name="${this._escape(card.name)}"
              ${inCart ? 'disabled' : ''}>
              ${addLabel}
            </button>
          </div>
        </div>`;
    }

    _renderOutOfStockRow(item) {
      const img = item.imageUrl || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png';
      const name = item.displayName || item.searchName;
      return `
        <div class="dcg-inv-card dcg-inv-card--oos">
          <img class="dcg-inv-card__img" src="${this._escape(img)}" alt="${this._escape(name)}" loading="lazy">
          <div class="dcg-inv-card__info">
            <div class="dcg-inv-card__name">${this._escape(name)}${item.setCode ? `<span class="dcg-set-badge">${this._escape(item.setCode)}</span>` : ''}</div>
            <div class="dcg-inv-card__meta">Requested ×${item.requested || 1} &mdash; see recommendations →</div>
          </div>
          <div class="dcg-inv-card__right">
            <span class="dcg-stock-badge dcg-stock-badge--none">Out of stock</span>
          </div>
        </div>`;
    }

    /* ---- Column 3: Recommendations ---- */
    _renderCol3(isReview) {
      if (!isReview) {
        return `
          <div class="dcg-col dcg-col--recs">
            <div class="dcg-col__hd">
              <div class="dcg-col__hd-left"><span class="dcg-col__num">③</span><span class="dcg-col__title">Recommendations</span></div>
            </div>
            <div class="dcg-col__body dcg-col__body--center">
              <p class="dcg-placeholder-text">Missing or out-of-stock cards will show alternatives here.</p>
            </div>
          </div>`;
      }

      const noStock  = this.state.rawResults?.noStock  || [];
      const notFound = this.state.rawResults?.notFound || [];
      const missing  = [...noStock, ...notFound];

      // Partially-fulfilled cards (in Inventory Results but qty < requested)
      // that have alternative printings available
      const partialWithAlts = (this.state.filteredFound || []).filter(c =>
        c._isPartial && (c._alternatives || []).length > 0
      );

      const withAlts    = [...missing.filter(c => (c._alternatives || []).length > 0), ...partialWithAlts];
      const withoutAlts = missing.filter(c => (c._alternatives || []).length === 0);

      if (missing.length === 0 && partialWithAlts.length === 0) {
        return `
          <div class="dcg-col dcg-col--recs">
            <div class="dcg-col__hd">
              <div class="dcg-col__hd-left"><span class="dcg-col__num">③</span><span class="dcg-col__title">Recommendations</span></div>
              <span class="dcg-badge dcg-badge--ok">All found</span>
            </div>
            <div class="dcg-col__body dcg-col__body--center">
              <div class="dcg-all-good">
                <span class="dcg-all-good__icon">✓</span>
                <strong>Every card in stock</strong>
                <p>Your full decklist was found in our store. Nothing to substitute.</p>
              </div>
            </div>
          </div>`;
      }

      return `
        <div class="dcg-col dcg-col--recs">
          <div class="dcg-col__hd">
            <div class="dcg-col__hd-left"><span class="dcg-col__num">③</span><span class="dcg-col__title">Recommendations</span></div>
            <span class="dcg-badge dcg-badge--warn">${missing.length} missing</span>
          </div>
          <div class="dcg-col__body">
            ${withAlts.length > 0 ? `
              <div class="dcg-sec-label dcg-sec-label--alt">Alternative Printings</div>
              <div class="dcg-sec-action-row">
                <span class="dcg-sec-action-info"><strong>${withAlts.length}</strong> alternative${withAlts.length !== 1 ? 's' : ''} available</span>
                <button class="dcg-sec-add-btn dcg-sec-add-btn--alt" data-action="add-all-recs">Add all alternatives</button>
              </div>
              ${withAlts.map(item => this._renderRecCard(item)).join('')}
            ` : ''}
            ${withoutAlts.length > 0 ? `
              <div class="dcg-sec-label dcg-sec-label--missing">No Alternatives Found</div>
              ${withoutAlts.map(item => this._renderNoAltCard(item)).join('')}
            ` : ''}
          </div>
        </div>`;
    }

    _renderRecCard(item) {
      const alts = item._alternatives || [];
      const img  = item.imageUrl || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png';
      const name = item.displayName || item.searchName;
      return `
        <div class="dcg-rec-card" data-rec-card="${this._escape(item.searchName)}">
          ${this._renderRecCardInner(item)}
        </div>`;
    }

    _renderRecCardInner(item) {
      const alts = item._alternatives || [];
      if (alts.length === 0) return '<div class="dcg-rec-card__empty">No alternatives found.</div>';

      const requested = item._remainingRequested || item.requestedAmount || item.requested || 1;

      // Exclude variants already in the cart — those rows disappear automatically
      // on the next render once the player adds them.
      const cartIds = new Set(this.state.cartItems.map(c => c.shopifyId));

      // Dynamically compute remaining needed: subtract any alternatives already
      // added to the cart from the original requested amount.
      let addedFromAlts = 0;
      alts.forEach(product => {
        (product.variants || []).forEach(v => {
          if (cartIds.has(v.shopifyId)) {
            const ci = this.state.cartItems.find(i => i.shopifyId === v.shopifyId);
            addedFromAlts += ci ? (ci.quantityUsed || 1) : 1;
          }
        });
      });
      const remaining = Math.max(0, requested - addedFromAlts);
      const allOptions = [];
      alts.forEach(product => {
        (product.variants || []).filter(v => v.quantity > 0 && !cartIds.has(v.shopifyId)).forEach(v => {
          allOptions.push({
            label:     product.title + (v.title && v.title !== 'Default Title' ? ' \u2014 ' + v.title : ''),
            img:       product.img || '',
            shopifyId: v.shopifyId,
            name:      product.title + ' ' + v.title,
            price:     v.price,
            quantity:  v.quantity,
          });
        });
      });

      /* DCG 03 AUG 2026 — a card can be satisfied two ways, and this only
         checked one of them.

           allOptions.length === 0 : every alternate printing is already in the
                                     cart, so there is nothing left to offer.
           remaining <= 0          : the customer has taken enough to cover the
                                     shortfall, even though OTHER printings are
                                     still available and unadded.

         Only the first was handled. Adding 2 Boss's Orders satisfied the deck
         but left other printings in allOptions, so the card kept rendering and
         the bar read "Missing: Boss's Orders x0" — a nonsense count, and no
         resolved state. Cards with a single alternate happened to work because
         adding it emptied allOptions, which is why this looked inconsistent. */
      if (remaining <= 0 || allOptions.length === 0) {
        return '<div class="dcg-rec-card__empty dcg-rec-card__empty--added">\u2713 Added to cart</div>';
      }

      /* One row per alternative. DCG 03 AUG 2026 — the fixed "+N →" button is
         now a quantity stepper matching the cart and inventory controls, so a
         customer can take e.g. 2 of a printing rather than the whole shortfall.
         Default is still the amount needed; max is that printing's real stock,
         which the Liquid inventory endpoint now supplies exactly.

         Chosen values live in this.state.altQty keyed by shopifyId so they
         survive the re-render triggered by adding a different row. */
      this.state.altQty = this.state.altQty || {};
      const rowsHtml = allOptions.map(opt => {
        const canFulfil = Math.min(opt.quantity, remaining);
        const stored    = this.state.altQty[opt.shopifyId];
        const chosen    = Math.max(1, Math.min(
          stored !== undefined ? stored : canFulfil,
          Math.max(1, opt.quantity)
        ));
        const altData   = JSON.stringify({
          shopifyId: opt.shopifyId, name: opt.name, img: opt.img,
          price: opt.price, quantity: opt.quantity,
          quantityUsed: chosen, _requestedAmount: requested,
          _searchName: item.searchName, displayName: opt.label,
        }).replace(/'/g, '&#39;');
        const img = opt.img || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png';
        return `
          <div class="dcg-alt-row">
            <img class="dcg-alt-row__img" src="${this._escape(img)}" alt="${this._escape(opt.label)}"
              loading="lazy" data-action="preview-card" data-src="${this._escape(img)}">
            <div class="dcg-alt-row__info">
              <span class="dcg-alt-row__name">${this._escape(opt.label)}</span>
              <span class="dcg-alt-row__meta">${opt.quantity} in stock &mdash; ${formatMoney(opt.price)}</span>
            </div>
            <div class="dcg-alt-row__controls">
              <div class="dcg-alt-qty dcg-inv-qty" data-alt-id="${this._escape(String(opt.shopifyId))}">
                <button class="dcg-inv-qty__btn" data-action="alt-qty-minus"
                  data-alt-id="${this._escape(String(opt.shopifyId))}"
                  ${chosen <= 1 ? 'disabled' : ''} aria-label="Decrease quantity">&minus;</button>
                <input class="dcg-inv-qty__input" type="number" inputmode="numeric"
                  value="${chosen}" min="1" max="${opt.quantity}"
                  data-action="alt-qty-input" data-alt-id="${this._escape(String(opt.shopifyId))}"
                  data-max="${opt.quantity}" aria-label="Quantity">
                <button class="dcg-inv-qty__btn" data-action="alt-qty-plus"
                  data-alt-id="${this._escape(String(opt.shopifyId))}" data-max="${opt.quantity}"
                  ${chosen >= opt.quantity ? 'disabled' : ''} aria-label="Increase quantity">+</button>
              </div>
              <button class="dcg-alt-row__add" data-action="add-rec-to-cart" data-alt='${altData}'>
                Add &rarr;
              </button>
            </div>
          </div>`;
      }).join('');

      return `
        <div class="dcg-rec-card__missing-bar">${item._isPartial
          ? 'Need ' + remaining + ' more: ' + this._escape(item.displayName || item.name || item.searchName) + ' (' + item.fulfilledAmount + ' in stock)'
          : 'Missing: ' + this._escape(item.displayName || item.searchName) + ' \xd7' + remaining
        }</div>
        <div class="dcg-alt-list">${rowsHtml}</div>`;
    }

    _renderNoAltCard(item) {
      const img  = item.imageUrl || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png';
      const name = item.displayName || item.searchName;
      const isWishlisted = this._isWishlisted(name);
      const isNotified   = this._isNotified(name);

      return `
        <div class="dcg-no-alt-card" data-rec-card="${this._escape(item.searchName)}">
          <div class="dcg-no-alt-card__top">
            <img class="dcg-no-alt-card__img" src="${this._escape(img)}" alt="${this._escape(name)}" loading="lazy">
            <div class="dcg-no-alt-card__info">
              <div class="dcg-no-alt-card__name">${this._escape(name)}${item.setCode ? `<span class="dcg-set-badge">${this._escape(item.setCode)}</span>` : ''}</div>
              <div class="dcg-no-alt-card__status">Not in store</div>
              <button class="dcg-wishlist-btn ${isWishlisted ? 'is-wishlisted' : ''}"
                data-action="toggle-wishlist"
                data-card-name="${this._escape(name)}"
                data-card-img="${this._escape(img)}">
                ${isWishlisted ? '♥ Wishlisted' : '♡ Add to wishlist'}
              </button>
            </div>
          </div>
          ${isNotified
            ? `<div class="dcg-notify-done">✓ We'll notify you at ${this._escape(this._getNotifyStore()[name]?.email || '')}</div>`
            : `<div class="dcg-notify-form">
                <label class="dcg-notify-form__label">Notify me when back in stock</label>
                <div class="dcg-notify-form__row">
                  <input class="dcg-notify-form__input" type="email" placeholder="your@email.com"
                    data-notify-card="${this._escape(name)}" autocomplete="email">
                  <button class="dcg-notify-form__btn" data-action="notify-submit"
                    data-card-name="${this._escape(name)}">Notify me</button>
                </div>
              </div>`}
        </div>`;
    }

    /* ---- Cart Section ---- */
    _renderCartSection() {
      const items = this.state.cartItems;
      const total = this._cartTotal();

      return `
        <div class="dcg-cart-section">
          <div class="dcg-cart-section__hd">
            <span class="dcg-col__num">④</span>
            <span class="dcg-col__title">Your Cart</span>
            ${items.length > 0 ? `<span class="dcg-badge dcg-badge--ok">${items.reduce((s,i)=>s+i.quantityUsed,0)} cards</span>` : ''}
            <span class="dcg-cart-section__hint">Cards are added to your Shopify cart when you complete checkout</span>
          </div>
          <div class="dcg-cart-grid" id="dcg-cart-grid">
            ${items.length === 0 ? `
              <div class="dcg-cart-empty">
                <p>No cards added yet.<br>Use the <strong>Add</strong> buttons in columns ② and ③.</p>
              </div>
            ` : items.map(item => this._renderCartTile(item)).join('')}
          </div>
          <div class="dcg-cart-footer">
            <div class="dcg-cart-footer__total">
              <span class="dcg-cart-footer__total-label">Order Total</span>
              <span class="dcg-cart-footer__total-amt">${formatMoney(total)}</span>
            </div>
            <div class="dcg-cart-footer__right">
              <p class="dcg-cart-footer__note">Quantities are limited to available stock on hand.</p>
              <button class="dcg-btn dcg-btn--primary" data-action="open-verify" ${items.length === 0 ? 'disabled' : ''}>
                Review &amp; Checkout &rarr;
              </button>
            </div>
          </div>
        </div>`;
    }

    _renderCartTile(item) {
      const img    = item.img || 'https://storage.googleapis.com/binderpos-libraries/cardNoImage.png';
      // Cap at real stock (item.quantity — updated by probe to real Shopify count,
      // or 1 conservative fallback pre-probe) OR the player's requested amount.
      const maxQty = Math.min(item.quantity, item._requestedAmount || item.quantity);

      return `
        <div class="dcg-cart-tile" data-shopify-id="${item.shopifyId}">
          <img class="dcg-cart-tile__img" src="${this._escape(img)}" alt="${this._escape(item.name)}"
            loading="lazy" data-action="preview-card" data-src="${this._escape(img)}">
          <div class="dcg-cart-tile__info">
            <div class="dcg-cart-tile__name" title="${this._escape(item.name)}">${this._escape(item.name)}</div>
            <div class="dcg-cart-tile__price">${formatMoney(item.price * item.quantityUsed)}</div>
          </div>
          <div class="dcg-cart-tile__controls">
            <div class="dcg-qty">
              <button class="dcg-qty__btn" data-action="cart-qty-minus" data-shopify-id="${item.shopifyId}" ${item.quantityUsed <= 1 ? 'disabled' : ''}>&#8722;</button>
              <input class="dcg-qty__input" type="number" min="1" max="${maxQty}" value="${item.quantityUsed}"
                data-action="cart-qty-input" data-cart-qty-input="${item.shopifyId}" data-shopify-id="${item.shopifyId}">
              <button class="dcg-qty__btn" data-action="cart-qty-plus" data-shopify-id="${item.shopifyId}" ${item.quantityUsed >= maxQty ? 'disabled' : ''}>&#43;</button>
            </div>
            <button class="dcg-cart-tile__remove" data-action="remove-cart-item" data-shopify-id="${item.shopifyId}" title="Remove">&#x2715;</button>
          </div>
        </div>`;
    }

    /* ---- Add-all duplicate warning ---- */
    _renderAddAllWarning() {
      return `
        <div class="dcg-warning-backdrop" data-action="dismiss-add-all-warning">
          <div class="dcg-warning-modal">
            <h3 class="dcg-warning-modal__title">Some cards already in cart</h3>
            <p class="dcg-warning-modal__body">Some of these cards are already in your cart. Do you want to update their quantities?</p>
            <div class="dcg-warning-modal__btns">
              <button class="dcg-btn dcg-btn--ghost" data-action="dismiss-add-all-warning">Cancel</button>
              <button class="dcg-btn dcg-btn--primary" data-action="force-add-all">Yes, update cart</button>
            </div>
          </div>
        </div>`;
    }

    /* ============================================================
       EVENT HANDLERS
    ============================================================ */
    _handleClick(e) {
      const el     = e.target.closest('[data-action]');
      if (!el) return;
      const action = el.dataset.action;

      switch (action) {
        case 'submit':
          this.submit();
          break;

        case 'add-all-found':
          this._addAllFound();
          break;

        case 'force-add-all':
          this._addAllFound(true);
          break;

        case 'dismiss-add-all-warning':
          this.state.pendingAddAll = null;
          this.render();
          break;

        /* Stepper — DCG 03 AUG 2026. Writes card._chosenQty, which
           _renderInventoryCard clamps to real stock and the add handler reads.
           Shortfall against the decklist quantity keeps the card flowing into
           Recommendations so alternate printings stay offered. */
        case 'inv-qty-minus':
        case 'inv-qty-plus':
        case 'inv-qty-input': {
          const nm = el.dataset.cardName;
          const c  = this.state.filteredFound.find(x => x.name === nm);
          if (c) {
            const avail = c.fulfilled?.[0]?.quantity || 0;
            const cur   = c._chosenQty !== undefined
              ? c._chosenQty
              : Math.min(c.requestedAmount || 1, avail);
            let next = cur;
            if (action === 'inv-qty-minus')      next = cur - 1;
            else if (action === 'inv-qty-plus')  next = cur + 1;
            else                                 next = parseInt(el.value, 10);
            if (!Number.isFinite(next)) next = cur;
            c._chosenQty = Math.max(1, Math.min(next, Math.max(1, avail)));
            this.render();
          }
          break;
        }

        case 'add-card-to-cart': {
          const cardName = el.dataset.cardName;
          const card = this.state.filteredFound.find(c => c.name === cardName);
          if (card && card.fulfilled.length > 0) {
            const exists = this.state.cartItems.some(i => i._searchName === cardName);
            if (!exists) {
              /* Use the stepper value rather than the decklist quantity.
                 _addToCart already clamps to min(variant.quantity, requested),
                 and variant.quantity is now the exact Shopify count. */
              const avail = card.fulfilled?.[0]?.quantity || 0;
              const want  = Math.max(1, Math.min(
                card._chosenQty !== undefined ? card._chosenQty : (card.requestedAmount || 1),
                Math.max(1, avail)
              ));
              card.fulfilled.filter(v => v.quantityUsed > 0).forEach(v => {
                this._addToCart(v, { requestedAmount: want, searchName: card.name });
              });
              this.render();
            }
          }
          break;
        }

        case 'add-all-recs':
          this._addAllAlts();
          break;

        // alt-select handler removed — replaced by individual add buttons per row

        /* Alternate-printing stepper — DCG 03 AUG 2026.
           Updated in place rather than via render(). The recommendations
           column scrolls and can be long; a full re-render resets its scroll
           position, which made stepping feel like the page jumped. The
           inventory stepper can re-render safely because those cards sit in a
           shorter column. */
        case 'alt-qty-minus':
        case 'alt-qty-plus':
        case 'alt-qty-input': {
          const id  = el.dataset.altId;
          const box = this.root.querySelector(`.dcg-alt-qty[data-alt-id="${CSS.escape(id)}"]`);
          if (!box) break;
          const input = box.querySelector('.dcg-inv-qty__input');
          const max   = parseInt(input.getAttribute('max'), 10) || 1;
          const cur   = parseInt(input.value, 10) || 1;
          let next = cur;
          if (action === 'alt-qty-minus')     next = cur - 1;
          else if (action === 'alt-qty-plus') next = cur + 1;
          else                                next = parseInt(el.value, 10);
          if (!Number.isFinite(next)) next = cur;
          next = Math.max(1, Math.min(next, max));

          this.state.altQty = this.state.altQty || {};
          this.state.altQty[id] = next;
          input.value = next;
          box.querySelector('[data-action="alt-qty-minus"]').disabled = next <= 1;
          box.querySelector('[data-action="alt-qty-plus"]').disabled  = next >= max;

          /* Keep the sibling Add button's payload in step with the stepper. */
          const addBtn = box.parentElement?.querySelector('[data-action="add-rec-to-cart"]');
          if (addBtn && addBtn.dataset.alt) {
            try {
              const payload = JSON.parse(addBtn.dataset.alt);
              payload.quantityUsed = next;
              addBtn.dataset.alt = JSON.stringify(payload);
            } catch (err) { /* leave payload as-is if it will not parse */ }
          }
          break;
        }

        case 'add-rec-to-cart': {
          // Stop any Shopify/BinderPOS theme handlers from intercepting this click
          e.preventDefault();
          e.stopPropagation();
          try {
            // Each alternative row embeds its altData on data-alt of the button.
            const altRaw = el.dataset.alt;
            const alt    = altRaw ? JSON.parse(altRaw) : null;
            if (alt) {
              const exists = this.state.cartItems.find(i => i.shopifyId === alt.shopifyId);
              if (exists) {
                exists.quantityUsed = Math.min(exists.quantity, exists.quantityUsed + alt.quantityUsed);
              } else {
                this.state.cartItems.push({ ...alt });
              }

              // Save scroll positions synchronously BEFORE render() touches the DOM.
              // rAF is too late — the browser resets scroll as part of innerHTML replacement.
              const winY       = window.scrollY;
              const colBodies  = [...this.root.querySelectorAll('.dcg-col__body')];
              const colScrolls = colBodies.map(c => c.scrollTop);

              this.render();

              // Restore synchronously — scroll happens immediately after innerHTML is set.
              window.scrollTo(0, winY);
              this.root.querySelectorAll('.dcg-col__body').forEach((c, i) => {
                if (colScrolls[i]) c.scrollTop = colScrolls[i];
              });
            }
          } catch { /* ignore */ }
          break;
        }

        case 'cart-qty-minus': {
          this._adjustCartQty(el.dataset.shopifyId, -1);
          break;
        }

        case 'cart-qty-plus': {
          this._adjustCartQty(el.dataset.shopifyId, 1);
          break;
        }

        case 'remove-cart-item':
          this._removeFromCart(el.dataset.shopifyId);
          break;

        case 'open-verify':
          this.openVerifyModal();
          break;

        case 'toggle-multiselect': {
          const target = el.dataset.target;
          const dd     = this.root.querySelector(`[data-dropdown="${target}"]`);
          if (dd) { dd.classList.toggle('is-open'); el.classList.toggle('is-open'); }
          break;
        }

        case 'toggle-wishlist': {
          const name  = el.dataset.cardName;
          const img   = el.dataset.cardImg;
          const added = this._toggleWishlist(name, img);
          el.textContent = added ? '♥ Wishlisted' : '♡ Add to wishlist';
          el.classList.toggle('is-wishlisted', added);
          this._showToast(added ? `${name} added to wishlist` : `${name} removed from wishlist`);
          break;
        }

        case 'notify-submit': {
          const cardName  = el.dataset.cardName;
          const inputEl   = this.root.querySelector(`[data-notify-card="${CSS.escape(cardName)}"]`);
          const email     = inputEl?.value?.trim() || '';
          if (!email || !email.includes('@')) {
            this._showToast('Please enter a valid email address');
            break;
          }
          this._saveNotify(cardName, email);
          this._showToast(`We'll notify you when ${cardName} is back in stock`);
          this.render();
          break;
        }

        case 'autocomplete-select': {
          const { handle, title, price } = el.dataset;
          this._selectAutocomplete(handle, title, price);
          break;
        }

        case 'preview-card':
          break; // handled in mouseover

        default:
          break;
      }
    }

    _handleChange(e) {
      const action = e.target.dataset.action;
      if (!action) return;

      if (action === 'select-game') {
        const newId = e.target.value;
        const game  = this.state.supportedGames.find(g => g.gameId === newId);
        if (game && (!isGameLive(game) || SUPPRESSED_GAME_IDS.has(game.gameId))) {
          e.target.value = this.state.selectedGameId;
          return;
        }
        this.state.selectedGameId = newId;
        this.state.selectedFormat = null;
        this.state.recommendations = {};
        if (this.state.stage === 'review') this._refilter();
        this.render();
      }

      if (action === 'select-format') {
        this.state.selectedFormat = e.target.value || null;
      }

      const msItem = e.target.dataset.multiselectItem;
      if (msItem) {
        const val     = e.target.value;
        const checked = e.target.checked;
        if (msItem === 'conditions') {
          this.state.selectedConditions = checked
            ? [...this.state.selectedConditions, val]
            : this.state.selectedConditions.filter(c => c !== val);
        } else if (msItem === 'printings') {
          this.state.selectedPrintings = checked
            ? [...this.state.selectedPrintings, val]
            : this.state.selectedPrintings.filter(p => p !== val);
        }
        const label = (() => {
          const sel = msItem === 'conditions' ? this.state.selectedConditions : this.state.selectedPrintings;
          return sel.length === 0 ? (msItem === 'conditions' ? 'Any condition' : 'Any printing') : `${sel.length} selected`;
        })();
        const trigger = this.root.querySelector(`[data-action="toggle-multiselect"][data-target="${msItem}"]`);
        if (trigger) trigger.childNodes[0] && (trigger.childNodes[0].textContent = label);
        if (this.state.stage === 'review') {
          this._refilter();
          this.render();
        }
      }
    }

    _handleInput(e) {
      const action = e.target.dataset.action;
      if (!action) return;

      if (action === 'decklist-input') {
        this.state.decklistText = e.target.value;
      }

      if (action === 'autocomplete-input') {
        this._scheduleAutocomplete(e.target.value);
      }

      if (action === 'cart-qty-input') {
        this._setCartQty(e.target.dataset.shopifyId, e.target.value);
      }
    }

    _handleMouseover(e) {
      const el = e.target.closest('[data-action="preview-card"]');
      if (!el) return;
      const src = el.dataset.src;
      if (!src) return;
      const preview = document.getElementById('dcg-card-preview');
      if (preview) {
        preview.src   = src;
        preview.style.left = (e.clientX + window.scrollX) + 'px';
        preview.style.top  = (e.clientY + window.scrollY) + 'px';
        preview.classList.add('is-visible');
      }
    }

    _handleMouseout(e) {
      const el = e.target.closest('[data-action="preview-card"]');
      if (!el) return;
      const preview = document.getElementById('dcg-card-preview');
      if (preview) preview.classList.remove('is-visible');
    }

    _onDocClick(e) {
      // Close autocomplete
      if (!e.target.closest('.dcg-autocomplete')) {
        this.state.autocompleteOpen = false;
        const dd = this.root.querySelector('.dcg-autocomplete__dropdown');
        if (dd) dd.hidden = true;
      }
      // Close multiselect dropdowns
      this.root.querySelectorAll('.dcg-multiselect__dropdown.is-open').forEach(dd => {
        const trigger = dd.previousElementSibling;
        if (!trigger?.contains(e.target) && !dd.contains(e.target)) {
          dd.classList.remove('is-open');
          if (trigger) trigger.classList.remove('is-open');
        }
      });
    }

    /* ---- Utility ---- */
    _focusTextarea() {
      const el = this.root.querySelector('.dcg-textarea');
      if (el) el.focus();
    }

    _showToast(msg) { showGlobalToast(msg); }

    _escape(str) {
      if (str == null) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  function showGlobalToast(msg, duration = 2500) {
    let toast = document.getElementById('dcg-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'dcg-toast'; toast.className = 'dcg-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('is-visible');
    clearTimeout(toast._timer);
    toast._timer = setTimeout(() => toast.classList.remove('is-visible'), duration);
  }

  /* ============================================================
     BOOT
  ============================================================ */
  function boot() {
    const root = document.getElementById('dcg-decklist-root');
    if (!root) return;
    const config = window.dcgDecklist || {};
    if (!config.permanentDomain) {
      root.innerHTML = '<p style="color:red;padding:20px;">Decklist: missing shop domain config.</p>';
      return;
    }
    new DecklistApp(root, config);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

})();